import type { TranslationKey } from './es-CR';

/**
 * en-US must define every key es-CR defines, in the same shape — enforced by
 * Record<TranslationKey, string> below, so a missing translation fails
 * typecheck instead of silently falling back at runtime.
 */
export const enUS: Record<TranslationKey, string> = {
  'app.name': 'LuParX',
  'app.tagline': 'Smart parking',

  'common.loading': 'Loading…',
  'common.error.generic': 'Something went wrong. Please try again.',
  'common.error.NO_ACTIVE_MEMBERSHIP': 'Your account does not belong to an active municipality yet. Ask an administrator for access.',
  'common.error.TENANT_CONTEXT_REQUIRED': 'Choose a municipality first to continue.',
  'common.error.network': 'Could not connect to the server.',
  'common.empty': 'No data to display.',
  'common.retry': 'Retry',
  'app.error.title': 'Something broke on this screen',
  'app.error.body':
    'This is not your fault and nothing that was saved has been lost. You can retry; if it keeps ' +
    'happening, pass this message to whoever maintains the platform.',
  'common.error.reference': 'Code {{code}} · Trace {{traceId}}',
  'common.error.referenceNoTrace': 'Code {{code}}',
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.confirm': 'Confirm',
  'common.back': 'Back',
  'common.next': 'Next',
  'common.search': 'Search',
  'common.filter': 'Filter',
  'common.actions': 'Actions',
  'common.status': 'Status',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.close': 'Close',
  'common.understood': 'Got it',
  'common.select.placeholder': 'Select an option',
  'common.optional': 'optional',
  'common.languageSwitcher.label': 'Change language',

  'auth.portal.citizen.title': 'Citizen portal',
  'auth.portal.admin.title': 'Municipal administration',
  'auth.portal.inspector.title': 'Enforcement',
  'auth.portal.platform.title': 'Platform back-office',
  'auth.hero.description':
    'Manage your vehicle parking, fines and payments from a single account, at any affiliated municipality.',

  'auth.login.title': 'Sign in to your account',
  'auth.login.emailLabel': 'Email address',
  'auth.login.passwordLabel': 'Password',
  'auth.login.passwordToggle.show': 'Show password',
  'auth.login.passwordToggle.hide': 'Hide password',
  'auth.login.submit': 'Sign in',
  'auth.login.forgotPassword': 'Forgot your password?',
  'auth.login.noAccount': "Don't have an account?",
  'auth.login.registerLink': 'Create account',
  'auth.login.platformNoRegister': 'Accounts for this portal are created by a platform administrator.',

  'auth.login.error.invalidCredentials': 'Incorrect email or password.',
  'auth.login.error.network': 'Could not reach the server. Check your connection or whether the service is running.',
  'auth.login.error.tooManyAttempts': 'Too many attempts. Wait a few minutes before trying again.',
  'auth.login.error.server': 'The server could not process the sign-in.',

  'auth.forgotPassword.title': 'Recover your password',
  'auth.forgotPassword.description':
    'Enter your email address and we will send you instructions to reset your password.',
  'auth.forgotPassword.emailLabel': 'Email address',
  'auth.forgotPassword.submit': 'Send instructions',
  'auth.forgotPassword.success': 'If the email exists, you will receive instructions shortly.',
  'auth.forgotPassword.checkInbox': 'Check your inbox and your spam folder. The link expires in 1 hour.',
  'auth.forgotPassword.backToLogin': 'Back to sign in',

  'auth.resetPassword.title': 'Reset password',
  'auth.resetPassword.newPasswordLabel': 'New password',
  'auth.resetPassword.confirmPasswordLabel': 'Confirm password',
  'auth.resetPassword.submit': 'Reset password',
  'auth.resetPassword.success': 'Your password was updated successfully.',
  'auth.resetPassword.redirecting': 'Taking you to sign in…',
  'auth.resetPassword.goToLogin': 'Sign in now',

  'auth.register.title': 'Create account',
  'auth.register.submit': 'Sign up',
  'auth.register.success.pendingApproval':
    'Your registration was received. An administrator must approve your access before you can sign in.',
  'auth.register.success.active': 'Your account was created. You can now sign in.',
  'auth.register.loginLink': 'Already have an account? Sign in',
  'auth.register.section.name': 'Full name',
  'auth.register.section.identityDocument': 'Identity document',
  'auth.register.section.address': 'Address',
  'auth.register.section.phone': 'Phone',
  'auth.register.section.nationality': 'Nationality',
  'auth.register.section.email': 'Email address',
  'auth.register.section.birthDate': 'Date of birth',
  'auth.register.section.account': 'Account details',

  'auth.logout.action': 'Sign out',

  'user.field.givenName': 'Given name',
  'user.field.familyName': 'Family name',
  'user.field.secondFamilyName': 'Second family name',
  'user.field.identityDocument.countryCode': 'Issuing country',
  'user.field.identityDocument.type': 'Document type',
  'user.field.identityDocument.number': 'Document number',
  'user.field.address.country': 'Country',
  'user.field.address.line1': 'Address line 1',
  'user.field.address.line2': 'Address line 2',
  'user.field.address.postalCode': 'Postal code',
  'user.field.address.loadError': 'This list could not be loaded.',
  'user.field.phone.countryCode': 'Country',
  'user.field.phone.nationalNumber': 'Phone number',
  'user.field.nationalityCode': 'Nationality',
  'user.field.email': 'Email address',
  'user.field.birthDate': 'Date of birth',
  'user.field.password': 'Password',
  'user.field.confirmPassword': 'Confirm password',
  'user.field.tenant': 'Municipality',
  'user.field.tenant.none': 'No municipality for now',

  'document.type.NATIONAL_ID': 'National ID',
  'document.type.FOREIGN_RESIDENT_ID': 'Foreign resident ID',
  'document.type.PASSPORT': 'Passport',
  'document.type.TAX_ID': 'Tax ID',
  'document.type.OTHER': 'Other document',

  'address.level.province': 'Province',
  'address.level.canton': 'Canton',
  'address.level.district': 'District',
  'address.level.state': 'State/Province',
  'address.level.county': 'County',
  'address.level.city': 'City',

  'terms.accept': 'I accept the terms and conditions and the privacy policy',
  'terms.viewLink': 'View terms',

  'validation.required': 'This field is required.',
  'validation.email.invalid': 'Enter a valid email address.',
  'validation.password.tooShort': 'Password must be at least {{min}} characters.',
  'validation.password.mismatch': 'Passwords do not match.',
  'validation.phone.invalid': 'Enter a valid phone number.',
  'validation.document.invalid': "The document number doesn't match the expected format.",
  'validation.age.tooYoung': 'You must be at least {{min}} years old to register.',
  'validation.date.invalid': 'Enter a valid date.',
  'validation.terms.required': 'You must accept the terms and conditions.',
  'validation.code.length': 'The code must be {{length}} digits long.',
  'validation.generic': 'The value entered is not valid.',
  'validation.form.incomplete.one': 'One field is missing: “{{field}}”. We took you to it.',
  'validation.form.incomplete.many':
    '{{count}} fields are missing. The first one is “{{field}}”: we took you to it.',

  'pagination.of': 'of',
  'pagination.itemsPerPage': 'Rows per page',
  'pagination.next': 'Next page',
  'pagination.previous': 'Previous page',
  'pagination.page': 'Page',
  'pagination.resultCount.zero': 'No results',
  'pagination.resultCount.one': '{{count}} result',
  'pagination.resultCount.other': '{{count}} results',

  'tenant.sheet.title': 'Municipality',
  'tenant.sheet.description': 'Zones, rates and your balance depend on the one you choose.',
  'tenant.sheet.catalogDescription':
    'Choose any municipality in the country. If it is new to you, you join with a zero balance and its own zones and rates.',
  'tenant.sheet.searchLabel': 'Search municipalities',
  'tenant.sheet.searchPlaceholder': 'Search municipalities',
  'tenant.sheet.memberBadge': 'Already yours',
  'tenant.sheet.noMatches.title': 'No matches',
  'tenant.sheet.noMatches.description': 'No municipality matches "{{query}}".',
  'tenant.selector.title': 'Choose your municipality',
  'tenant.selector.description':
    'Your account belongs to several municipalities. Zones, rates and your balance depend on the one you pick.',
  'tenant.selector.continue': 'Continue',
  'tenant.selector.noneAvailable': "You don't have approved access to any municipality yet.",
  'tenant.selector.empty.title': 'No municipalities yet',
  'tenant.selector.error.notAvailable':
    'That municipality is no longer available to your account. Pick another one or ask an administrator for access.',
  'tenant.selector.error.notOpenToCitizens':
    'This municipality does not accept self-service sign-ups. Ask its administration for access.',
  'tenant.selector.signOut': 'Sign out',
  'tenant.badge.changeAction': 'change municipality',
  'tenant.switch.action': 'Change',
  'tenant.switch.description': 'Changing municipality updates the zones, the rates and your balance.',
  'tenant.switch.parkingHint': 'Zones, rates and the bay code format belong to this municipality.',
  'tenant.membership.status.ACTIVE': 'Active',
  'tenant.membership.status.PENDING_APPROVAL': 'Pending approval',
  'tenant.membership.status.REJECTED': 'Rejected',
  'tenant.membership.status.REVOKED': 'Revoked',

  'admin.search.placeholder': 'Search…',
  'admin.search.destinations': 'Go to',
  'admin.search.inUsers': 'Search in Users',
  'admin.search.empty': 'Nothing matches that.',
  'admin.header.account': 'Your account',
  'admin.users.title': 'Users',
  'admin.users.subtitle': 'Registered people and their access to LuParX.',
  'admin.users.column.accountStatus': 'Account status',
  'admin.users.noRole': 'No role assigned',

  'admin.zones.title': 'Zones',
  'admin.zones.description':
    "The sectors this municipality charges in. A zone is never deleted: it is deactivated, and everything ever paid in it stays readable.",
  'admin.zones.create': 'Create zone',
  'admin.zones.edit': 'Edit zone',
  'admin.zones.empty': 'This municipality has no zones yet.',
  'admin.zones.created': 'Zone created.',
  'admin.zones.updated': 'Zone updated.',
  'admin.zones.error.codeTaken': 'Another zone in this municipality already uses that code.',
  'admin.zones.error.generic': 'The change could not be saved.',
  'admin.zones.column.zone': 'Zone',
  'admin.zones.column.spaces': 'Bays',
  'admin.zones.column.status': 'Status',
  'admin.zones.spaceCount.one': '{{count}} bay',
  'admin.zones.spaceCount.other': '{{count}} bays',
  'admin.zones.status.active': 'Operating',
  'admin.zones.status.inactive': 'Deactivated',
  'admin.zones.action.spaces': 'Bays',
  'admin.zones.action.activate': 'Activate',
  'admin.zones.deactivate.title': 'Deactivate this zone?',
  'admin.zones.deactivate.body':
    'It stops accepting new stays immediately. Stays already running continue, and citations and ' +
    'history are kept. The zone is not deleted: you can activate it again whenever you want.',
  'admin.zones.activate.title': 'Activate this zone?',
  'admin.zones.activate.body': 'It accepts stays again with its configured rates and schedule.',
  'admin.zones.deactivate.confirm': 'Deactivate zone',
  'admin.zones.activate.confirm': 'Activate zone',
  'admin.zones.field.state': 'State',
  'admin.zones.state.active': 'Operating',
  'admin.zones.state.inactive': 'Inactive',
  'admin.zones.action.deactivate': 'Deactivate',
  'admin.zones.action.rules': 'Zone rules',
  'admin.zones.rules.title': 'Rules for {{zone}}',
  'admin.zones.rules.notice':
    'Anything you leave blank is inherited from the municipality, and stays inherited if the municipality changes it. The grey text in each field is what applies today.',
  'admin.zones.rules.saved': 'Zone rules saved.',
  'admin.zones.rules.inheritHint': 'Applies today: {{value}}',
  'admin.zones.rules.incrementsLabel': 'Durations sold here (minutes, comma separated)',
  'admin.zones.rules.minLabel': 'Shortest stay',
  'admin.zones.rules.maxLabel': 'Longest stay',
  'admin.zones.rules.maxHint': 'The lever that manages rotation: two hours downtown, more on the edges.',
  'admin.zones.rules.freeLabel': 'Courtesy minutes',
  'admin.zones.rules.freeHint': 'Granted once per plate per day. Zero means no courtesy.',
  'admin.zones.rules.ownScheduleLabel': 'This zone keeps its own charging hours',
  'admin.zones.rules.dayFree': 'Not charged',
  'admin.zones.rules.freeZoneWarning':
    'With its own timetable and no band at all, this zone never charges. That is valid — a free zone — but it should be on purpose.',
  'admin.zones.rules.error.band': 'Check the hours for {{day}}: the closing time has to be later than the opening one.',
  'admin.zones.rules.error.invalid': 'Check the numbers: courtesy cannot exceed the longest stay.',
  'admin.zones.field.code': 'Code',
  'admin.zones.field.codeHint': 'What the zone is called in reports and on the radio. It cannot be changed later.',
  'admin.zones.field.codeFixed': 'The code does not change: it is what every report of what was charged here is grouped by.',
  'admin.zones.field.name': 'Name',
  'admin.zones.field.description': 'Description',

  'admin.spaces.title': 'Bays',
  'admin.spaces.description':
    'The bays of a zone. The code is the one painted on the ground; if the municipality repaints it, correct it here and the bay stays the same bay.',
  'admin.spaces.create': 'Add bay',
  'admin.spaces.empty': 'This zone has no bays yet.',
  'admin.spaces.created': 'Bay added.',
  'admin.spaces.updated': 'Bay updated.',
  'admin.spaces.pickZone': 'Choose a zone to see its bays.',
  'admin.spaces.zoneLabel': 'Zone',
  'admin.spaces.zonePlaceholder': 'Select a zone',
  'admin.spaces.search.label': 'Search by code',
  'admin.spaces.search.placeholder': 'For example {{example}}',
  'admin.spaces.search.hint':
    'Searches the whole municipality, even with no zone chosen. From two characters.',
  'admin.spaces.search.empty': 'No bay starts with “{{query}}”.',
  'admin.spaces.search.more': 'There are more. Type another character to narrow it down.',
  'admin.spaces.search.showingOne': 'Showing only bay {{code}}.',
  'admin.spaces.search.clear': "See all the zone's bays",
  'admin.spaces.column.code': 'Code',
  'admin.spaces.column.status': 'Status',
  'admin.spaces.status.AVAILABLE': 'In service',
  'admin.spaces.status.OUT_OF_SERVICE': 'Out of service',
  'admin.spaces.action.outOfService': 'Take out of service',
  'admin.spaces.action.backInService': 'Put back in service',
  'admin.spaces.action.editCode': 'Edit code',
  'admin.spaces.editCode.title': 'Edit the bay code',
  'admin.spaces.editCode.current': 'Current code: {{code}}',
  'admin.spaces.editCode.notice':
    'This is the same bay: it keeps its history, its zone and its status. Stays already paid and citations already issued keep showing the code they carried at the time.',
  'admin.spaces.renamed': 'Code updated. Anything already charged keeps the previous code.',
  'admin.spaces.codeHint': "This municipality's format, for example {{example}}.",
  'admin.spaces.error.codeTaken': 'A bay in this municipality already uses that code.',
  'admin.spaces.error.codeInvalid': "That code doesn't match this municipality's format ({{example}}).",

  'admin.tariffs.grid.title': 'Price per duration',
  'admin.tariffs.grid.description':
    'What a citizen is charged today for each duration in each sector. Tap a cell to give that duration a price of its own; without one, the zone\u2019s base tariff prices it.',
  'admin.tariffs.fromBase': 'from the base',
  'admin.tariffs.base.label': 'Base tariff',
  'admin.tariffs.base.value': 'Base: {{amount}} per {{minutes}} min',
  'admin.tariffs.base.missing': 'No base tariff — set one',
  'admin.tariffs.noDurations': 'This municipality sells no duration yet. They are set on the Parking policy screen.',
  'admin.tariffs.column.what': 'What',
  'admin.tariffs.action.clear': 'Remove its own price',
  'admin.tariffs.set.baseTitle': 'Base tariff for {{zone}}',
  'admin.tariffs.set.rungTitle': '{{duration}} in {{zone}}',
  'admin.tariffs.set.baseBody':
    'The zone\u2019s foundation: it charges per started block and prices every duration without one of its own, including a citizen\u2019s saved minutes.',
  'admin.tariffs.set.rungBody':
    'This amount IS the price of that duration, never multiplied. It replaces what the base would charge.',
  'admin.tariffs.set.inheriting': 'Today the base prices that duration: {{price}}.',
  'admin.tariffs.saved.base': 'Base tariff updated in {{zone}}.',
  'admin.tariffs.saved.rung': '{{duration}} in {{zone}} now has its own price.',
  'admin.tariffs.cleared': '{{duration}} in {{zone}} goes back to the base price.',
  'admin.tariffs.action.other': 'Another duration…',
  'admin.tariffs.field.duration': 'Duration (minutes)',
  'admin.tariffs.field.durationHint': 'Any number of minutes.',
  'admin.tariffs.set.newTitle': 'Another duration in {{zone}}',
  'admin.tariffs.set.newNotice':
    'On saving, this duration also goes on sale across the municipality: a price for something nobody can buy is charged to nobody.',
  'admin.tariffs.error.generic': 'The server refused the change.',
  'admin.tariffs.error.network': 'The server could not be reached.',
  'admin.tariffs.error.unknownRoute':
    'The server does not recognise this operation. It may be running a build older than this screen.',
  'admin.tariffs.error.zoneGone': 'That zone no longer exists in this municipality.',
  'admin.tariffs.error.noRung': 'That duration no longer has a price of its own.',
  'admin.tariffs.error.invalid': 'The amount or the duration is not valid.',
  'admin.tariffs.error.forbidden': 'Your account cannot change tariffs in this municipality.',
  'admin.tariffs.title': 'Tariffs',
  'admin.tariffs.description':
    'What each duration costs in each zone. Any of them can have its own price; the ones that do not are priced by the zone\u2019s base tariff, per started block.',
  'admin.tariffs.unpriced':
    'These zones have no tariff in force, so nobody can park in them: {{zones}}. A citizen who tries has the stay refused.',
  'admin.tariffs.noZones': 'This municipality has no zones yet.',
  'admin.tariffs.set.submit': 'Set tariff',
  'admin.home.footer.product': 'LuParX · Municipal parking platform',
  'admin.home.kpi.inPeriod': 'Today',
  'admin.home.subtitle': 'Operational summary · {{date}}',
  'admin.home.system.api': 'System',
  'admin.home.system.database': 'Database',
  'admin.home.system.enforcement': 'Enforcement',
  'admin.home.system.gateway': 'Payment gateway',
  'admin.home.system.alerts': 'Alerts',
  'admin.home.system.up': 'Operational',
  'admin.home.system.down': 'Not responding',
  'admin.home.system.unknown': 'Could not be checked',
  'admin.home.system.enforcementActive': '{{count}} on duty today',
  'admin.home.system.enforcementIdle': 'No activity today',
  'admin.home.system.noSource': 'No check available',
  'admin.home.system.noAlerts': 'No alerts',
  'admin.home.system.failedPayments': '{{count}} payments declined today',
  'admin.home.kpi.revenue': 'Revenue today',
  'admin.home.kpi.vsYesterday': '{{delta}} vs yesterday',
  'admin.home.kpi.noComparison': 'No comparison available',
  'admin.home.greeting': 'Hello, {{name}}',
  'admin.home.greeting.plain': 'Hello',
  'admin.home.where': 'Municipal administration · {{tenant}}',
  'admin.home.kpi.registeredUsers': 'Registered users',
  'admin.home.kpi.registered': 'Accounts in this municipality',
  'admin.home.occupancy.total': 'Total occupancy',
  'admin.home.occupancy.detail': 'See detail',
  'admin.home.occupancy.noBase': 'No bays to measure it against',
  'admin.home.rank.title': 'Most used zones',
  'admin.home.rank.detail': 'See detail',
  'admin.home.rank.empty': 'No zones with numbered bays yet.',
  'admin.home.occupancy.spaces': '{{activas}} / {{bahias}} spaces',
  'admin.home.revenue.weekTotal': 'Week total',
  'admin.home.attention.title': 'Needs attention',
  'admin.home.attention.review': 'Review',
  'admin.home.attention.payments.one': '1 payment declined today',
  'admin.home.attention.payments.other': '{{count}} payments declined today',
  'admin.home.attention.noZones': 'No zone has numbered bays',
  'admin.home.system.title': 'Service status',
  // --- Roles and permissions (§3 of the functional guide, 24-09-2026) ---------------------------
  'nav.roles': 'Roles and permissions',
  'admin.roles.title': 'Roles and permissions',
  'admin.roles.subtitle': 'What each role can do in this municipality.',
  'admin.roles.readOnly':
    'This is the table the server enforces on every operation. You read it here and change it in code, where it stays versioned and reviewable.',
  'admin.roles.matrix.title': 'Permissions by role',
  'admin.roles.matrix.description': 'Open a role to see what it grants and what it does not, grouped by area.',
  'admin.roles.summary': '{{granted}} of {{total}} permissions',
  'admin.roles.expand': "Show {{role}}'s permissions",
  'admin.roles.collapse': "Hide {{role}}'s permissions",
  'admin.roles.group.people': 'People and access',
  'admin.roles.group.staff': 'Staff and roles',
  'admin.roles.group.tenant': 'Municipality and parking',
  'admin.roles.group.citations': 'Citations and enforcement',
  'admin.roles.group.finance': 'Finance and reconciliation',
  'admin.roles.group.security': 'Security and audit',
  'admin.roles.group.other': 'Other',
  'admin.roles.empty': 'No roles for this portal.',
  'admin.roles.column.role': 'Role',
  'admin.roles.column.portal': 'Portal',
  'admin.roles.granted': 'Granted',
  'admin.roles.denied': 'Not granted',
  'admin.roles.permission.USER_READ': 'View people',
  'admin.roles.permission.USER_WRITE': 'Edit people',
  'admin.roles.permission.USER_BLOCK': 'Block people',
  'admin.roles.permission.MEMBERSHIP_APPROVE': 'Approve memberships',
  'admin.roles.permission.ROLE_ASSIGN': 'Assign roles',
  'admin.roles.permission.ZONE_ASSIGN': 'Assign zones',
  'admin.roles.permission.AUDIT_READ': 'View audit trail',
  'admin.roles.permission.EXPORT_RUN': 'Export',
  'admin.roles.permission.TENANT_MANAGE': 'Manage the municipality',
  'admin.roles.permission.PLATFORM_MANAGE': 'Manage the platform',
  'admin.roles.permission.CITATION_ISSUE': 'Issue citations',
  'admin.roles.permission.CITATION_READ': 'View citations',
  'admin.roles.permission.CITATION_VOID': 'Annul citations',
  'admin.roles.permission.ENFORCEMENT_MANAGE': 'Manage enforcement',
  'admin.roles.permission.CITATION_INGEST': 'Ingest external citations',
  'admin.roles.permission.WALLET_TOPUP': 'Top up and reconcile',
  // --- Per-record history (§4) ------------------------------------------------------------------
  'admin.history.empty': 'No changes recorded for this record yet.',
  'admin.history.column.when': 'When',
  'admin.history.column.who': 'Who',
  'admin.history.column.what': 'Action',
  'admin.history.column.changes': 'Change',
  'admin.history.column.reason': 'Reason',
  'admin.history.system': 'System',
  'admin.history.inactive': 'Inactive',
  'admin.history.masked': 'protected value',
  'admin.audit.filter.module': 'Module',
  'admin.audit.filter.module.all': 'All modules',
  'admin.audit.module.parking-rate': 'Tariffs',
  'admin.audit.module.parking-zone': 'Zones',
  'admin.audit.module.parking-policy': 'Parking policy',
  'admin.audit.module.parking-space': 'Spaces',
  'admin.audit.module.parking-space-format': 'Space format',
  'admin.audit.module.citation': 'Citations',
  'admin.audit.module.exemption': 'Exemptions',
  'admin.audit.module.user': 'People',
  'admin.audit.module.membership': 'Memberships',
  'admin.audit.module.settlement': 'Reconciliation',
  'admin.tariffs.who.title': 'Who changed this tariff',
  'admin.tariffs.who.description': 'The change, who made it and when. Different from the version history below.',
  'admin.tariffs.who.zone': 'Zone',
  'admin.tariffs.who.pickZone': 'Pick a zone',
  'admin.tariffs.who.empty': 'This tariff has not been modified since it was created.',

  'admin.home.kpi.activeSessions': 'Active parkings',
  'admin.home.kpi.rightNow': 'Right now',
  'admin.home.kpi.citations': 'Citations issued',
  'admin.home.kpi.occupancy': 'Current occupancy',
  'admin.home.kpi.open': 'Open {{label}}',
  'admin.home.kpi.noBays': 'No figure',
  'admin.home.kpi.noBaysHint': 'No zone has numbered bays, so there is nothing to measure it against.',
  'admin.home.revenue.range': 'Last {{days}} days',
  'admin.home.revenue.title': 'Revenue this week',
  'admin.home.revenue.description': 'Captured each day, in the municipality\u2019s own calendar.',
  'admin.home.revenue.empty': 'No revenue recorded in this period.',
  'admin.home.revenue.columnDay': 'Day',
  'admin.home.revenue.columnAmount': 'Collected',
  'admin.home.zones.title': 'Live occupancy',
  'admin.home.zones.description': 'Running stays over bays in service.',
  'admin.home.zones.empty': 'This municipality has no zones yet.',
  'admin.home.zones.noBays': 'No bays',
  'admin.home.activity.title': 'Recent activity',
  'admin.home.activity.description': 'The latest in the municipality, from the audit trail.',
  'admin.home.activity.emptyTitle': 'Nothing has happened yet',
  'admin.home.activity.emptyBody':
    'As soon as a citation is issued, a top-up recorded or a tariff changed, it will show up here.',
  'admin.home.activity.system': 'System',
  'admin.home.activity.seeAll': 'See all',
  'admin.home.activity.PARKING_SESSION_STARTED': 'Parking started',
  'admin.home.activity.PARKING_SESSION_FINISHED': 'Parking finished',
  'admin.home.activity.USER_REGISTERED': 'User registered',
  'admin.home.activity.USER_PASSWORD_RESET_REQUESTED': 'Access reset requested',
  'admin.home.activity.PARKING_ZONE_CREATED': 'Zone created',
  'admin.home.activity.WALLET_TOPUP_RECORDED': 'Top-up recorded',
  'admin.home.activity.CITATION_ISSUED': 'Citation issued',
  'admin.home.activity.CITATION_PAID': 'Citation paid',
  'admin.home.activity.CITATION_CANCELLED': 'Citation voided',
  'admin.home.activity.CITATION_APPEAL_FILED': 'Appeal received',
  'admin.home.activity.CITATION_APPEAL_RESOLVED': 'Appeal resolved',
  'admin.home.activity.PLATE_EXEMPTION_GRANTED': 'Exemption granted',
  'admin.home.activity.PARKING_SPACE_CREATED': 'Space registered',
  'admin.home.activity.PARKING_ZONE_UPDATED': 'Zone updated',
  'admin.home.activity.PARKING_RATE_UPDATED': 'Tariff updated',
  'admin.home.activity.PARKING_POLICY_UPDATED': 'Parking policy updated',
  'admin.home.activity.PARKING_SCHEDULE_UPDATED': 'Charging schedule updated',
  'admin.home.quick.title': 'Quick actions',
  'admin.home.quick.none': 'This account has none of these screens enabled.',
  'admin.home.quick.zone': 'Create zone',
  'admin.home.quick.zoneHint': 'Define a new charging area',
  'admin.home.quick.space': 'Register space',
  'admin.home.quick.spaceHint': 'Add bays to a zone',
  'admin.home.quick.plate': 'Look up a plate',
  'admin.home.quick.plateHint': 'Check citations and status',
  'admin.home.quick.report': 'Generate report',
  'admin.home.quick.reportHint': 'Export what was collected and enforced',
  'admin.home.noFigures.title': 'No access to the figures',
  'admin.home.noFigures.body':
    'Your account cannot see this municipality\u2019s revenue or audit trail. The screens you can open are below.',
  'admin.tariffs.confirm.title': 'Set this tariff?',
  'admin.tariffs.confirm.body':
    'This is what a citizen on the street will be charged from the moment you confirm. Stays ' +
    'already running keep the price they were paid at; the previous price moves to «Previous ' +
    'prices» and is not deleted.',
  'admin.tariffs.confirm.effectiveLabel': 'In force',
  'admin.tariffs.confirm.effectiveNow': 'From the moment you confirm, until the next change',
  'admin.tariffs.confirm.fromBase': '{{price}} (from the base)',
  'admin.tariffs.confirm.none': 'No price',
  'admin.tariffs.set.blockExample':
    'Charged by started block: a {{minutes}} min stay pays {{blocks}} blocks, {{amount}}.',
  'admin.tariffs.history.title': 'Earlier prices',
  'admin.tariffs.history.description':
    'The windows already closed. They are never edited: they are what priced the stays paid while they were in force.',
  'admin.tariffs.history.empty': 'No tariff has been changed yet.',
  'admin.tariffs.field.amount': 'Amount ({{currency}})',
  'admin.tariffs.field.amountNoCurrency': 'Amount',
  'admin.tariffs.field.minutes': 'Minutes',
  'admin.tariffs.field.minutesHint': 'The block that amount covers.',
  'admin.tariffs.column.zone': 'Zone',
  'admin.tariffs.column.price': 'Price',
  'admin.tariffs.column.from': 'From',
  'admin.tariffs.column.to': 'To',
  'admin.tariffs.price': '{{amount}} per {{minutes}} min',
  'admin.tariffs.openWindow': 'No closing date',

  'admin.staff.title': 'Staff',
  'admin.staff.description':
    "This municipality's staff. Deactivating somebody removes their access here; it touches neither their account nor the record of what they did.",
  'admin.staff.empty': 'This municipality has no staff yet.',
  'admin.staff.error': 'The action could not be completed.',
  'admin.staff.suspended': 'Access suspended.',
  'admin.staff.reactivated': 'Access restored.',
  'admin.staff.revoked': 'Access revoked.',
  'admin.staff.resetSent': 'They were sent a link to set a new password.',
  'admin.staff.zonesAssigned': 'Sectors updated.',
  'admin.staff.filter.status': 'Status',
  'admin.staff.filter.all': 'All statuses',
  'admin.staff.column.person': 'Person',
  'admin.staff.column.role': 'Role',
  'admin.staff.column.status': 'Status',
  'admin.staff.column.zones': 'Sectors',
  'admin.staff.column.lastLogin': 'Last access',
  'admin.staff.column.actions': 'Actions',
  'admin.staff.lastLogin.never': 'Never signed in',
  'admin.staff.zones.all': 'All',
  'admin.staff.zones.more': '+{{count}}',
  'admin.staff.zones.allTitle': 'No sector restriction: may work in all of them.',
  'admin.staff.zones.title': 'Assigned sectors',
  'admin.staff.zones.help':
    'An inspector can only check plates and issue citations in the sectors assigned to them.',
  'admin.staff.zones.noneMeansAll': 'With no sector ticked they may act anywhere in the municipality.',
  'admin.staff.add.title': 'Add staff member',
  'admin.staff.add.help':
    'Look the person up first: many people already have an account because they registered as citizens. If they exist, they get the post without being asked for their details again.',
  'admin.staff.add.searchBy': 'Search by',
  'admin.staff.add.by.document': 'By identity document',
  'admin.staff.add.by.email': 'By email',
  'admin.staff.add.exactHint': 'It has to be the whole, exact value; this is not a partial search.',
  'admin.staff.add.search': 'Search',
  'admin.staff.add.searchAgain': 'Look up somebody else',
  'admin.staff.add.notFound': 'Nobody on the platform has that. If this is their first time, open their file.',
  'admin.staff.add.createNew': 'Create a new file',
  'admin.staff.add.noAccessHere': 'They hold no access in this municipality yet.',
  'admin.staff.add.blocked': 'This account is blocked platform-wide. Unblock it before giving them a post.',
  'admin.staff.add.rolePerApp': 'One post per app: the same account can be an inspector on the street app and hold finance in this portal.',
  'admin.staff.add.appTaken': 'Already holds a post in that app',
  'admin.staff.add.grant': 'Grant access',
  'admin.staff.add.granted': 'Done: {{name}} is now {{role}} in this municipality. They were told by email.',
  'admin.staff.add.error.rateLimited': 'Too many lookups in a row. Wait a few minutes.',
  'admin.staff.add.error.documentInvalid': "That number doesn't match the format of the chosen document.",
  'admin.staff.add.error.alreadyHasAccess': 'That person already holds a post in that app in this municipality.',
  'invitation.title': 'You have been offered a post in {{tenant}}',
  'invitation.description': 'Post: {{role}}. The invitation was sent to {{email}}.',
  'invitation.expires': 'The invitation expires on {{date}}.',
  'invitation.passwordIsYours':
    'You choose the password. The municipality neither knows it nor can see it: that is what makes what you do in the system yours and not somebody else\'s.',
  'invitation.submit': 'Accept and create my account',
  'invitation.done.title': 'Done — your account is created',
  'invitation.done.body': 'You can now sign in to {{tenant}} as {{role}} with the invited address and the password you just chose.',
  'invitation.done.signIn': 'Go to sign in',
  'invitation.error.notFound': 'This invitation is no longer available. Ask the municipality to send it again.',
  'invitation.error.expired': 'This invitation has expired. Ask the municipality to send it again.',
  'invitation.error.alreadyAccepted': 'This invitation has already been used. Sign in with your account.',
  'invitation.error.documentTaken':
    'That identity document is already registered on the platform. Tell the municipality: you do not need an invitation, they can give the post to the account you already have.',
  'admin.staff.add.inviteHelp':
    'Invite them: the municipality supplies the address and the post, and they fill in their own details from a link. That way the identity document is typed by the person who holds it, which is the only thing that prevents the wrong identity.',
  'admin.staff.add.inviteEmailHint': 'The acceptance link goes there. It expires in 14 days.',
  'admin.staff.add.invite': 'Invite by email',
  'admin.staff.add.invited': 'Invitation sent to {{email}} as {{role}}.',
  'admin.staff.add.error.emailRegistered':
    'That address already has an account. Look it up here by email and give them the post without asking for their details again.',
  'admin.staff.invitations.title': 'Invitations',
  'admin.staff.invitations.description':
    'Posts offered to people who have no account yet. An invitation grants nothing: the access is created when the person accepts it.',
  'admin.staff.invitations.empty': 'No pending invitations.',
  'admin.staff.invitations.column.email': 'Email',
  'admin.staff.invitations.column.role': 'Post offered',
  'admin.staff.invitations.column.sent': 'Sent',
  'admin.staff.invitations.column.expires': 'Expires',
  'admin.staff.invitations.expired': 'Expired',
  'admin.staff.invitations.action.resend': 'Send again',
  'admin.staff.invitations.action.revoke': 'Withdraw',
  'admin.staff.invitations.resent': 'Sent again. The previous link stopped working.',
  'admin.staff.invitations.revoked': 'Invitation withdrawn.',
  'admin.staff.column.lastUsed': 'Post last used',
  'admin.staff.lastUsed.none': 'No recorded use',
  'admin.staff.lastUsed.personHint': 'The person signed in on {{date}}, through another app.',
  'admin.staff.action.changeRole': 'Change role',
  'admin.staff.changeRole.title': 'Change the post\'s role',
  'admin.staff.changeRole.body': "{{name}}'s post in {{app}}.",
  'admin.staff.changeRole.sameApp':
    'A role can only change within the same app. To give them a post in another app, add it as a new post: those are two posts, not a change.',
  'admin.staff.changeRole.done': 'Role updated.',
  'portal.citizen': 'Citizen app',
  'portal.platform': 'Platform',
  'portal.admin': 'Municipal portal',
  'portal.inspector': 'Enforcement app',
  'admin.staff.action.suspend': 'Deactivate',
  'admin.staff.action.reactivate': 'Restore access',
  'admin.staff.action.revoke': 'Revoke',
  'admin.staff.action.zones': 'Sectors',
  'admin.staff.action.resetAccess': 'Force a password change',
  'admin.staff.action.open': 'Open record',
  'admin.staff.actions.menu': 'Actions for this post',
  'admin.staff.actions.title': 'Actions',
  'admin.staff.actions.subject': '{{name}} · {{role}} · {{status}}',
  'admin.staff.actions.none': 'This post is revoked: no action applies to it.',
  'admin.staff.action.resetAccess.self':
    'You cannot force a password change on your own account: it would end your session.',
  'admin.staff.suspend.title': 'Deactivate this person?',
  'admin.staff.suspend.body':
    "{{name}} loses access to this municipality's portal. You can restore it whenever you like.",
  'admin.staff.suspend.keepsHistory':
    'The account is untouched: they go on using the app as a citizen. Their citations and everything they did are kept exactly as they are.',
  'admin.staff.suspend.reasonLabel': 'Reason',
  'admin.staff.suspend.reasonPlaceholder': 'Reason (optional)',
  'tenant.membership.status.SUSPENDED': 'Deactivated',
  'admin.users.searchPlaceholder': 'Search by name, email or document',
  'admin.users.column.name': 'Name',
  'admin.users.column.email': 'Email',
  'admin.users.column.status': 'Status',
  'admin.users.column.role': 'Role',
  'admin.users.column.tenant': 'Municipality',
  'admin.users.column.createdAt': 'Registered',
  'admin.users.filter.role': 'Role',
  'admin.users.filter.status': 'Status',
  'admin.users.filter.portal': 'Portal',
  'admin.users.filter.allRoles': 'All roles',
  'admin.users.filter.allStatuses': 'All statuses',
  'admin.users.filter.allPortals': 'All portals',
  'admin.users.empty': 'No users matched those criteria.',
  'admin.users.detail.title': 'User detail',

  // Roles, as a person reads them. The enum name is what the API speaks; this is what a screen shows.
  'role.PLATFORM_ADMIN': 'Platform administrator',
  'role.PLATFORM_SUPPORT': 'Platform support',
  'role.TENANT_ADMIN': 'Municipal administrator',
  'role.TENANT_FINANCE': 'Finance',
  'role.TENANT_FINANCE.detail': "Top-ups and the municipality's financial reports",
  'role.TENANT_SUPPORT': 'Municipal support',
  'role.TENANT_SUPPORT.detail': 'Reads users, fines and the audit log; changes nothing',
  'role.TENANT_INTEGRATION': 'Integration',
  'role.TENANT_INTEGRATION.detail': 'Credentials for systems that connect to the municipality',
  'role.INSPECTOR': 'Inspector',
  'role.INSPECTOR.detail': 'Checks plates and issues citations in the field',
  'role.INSPECTOR_LEAD': 'Lead inspector',
  'role.INSPECTOR_LEAD.detail': 'Everything an inspector does, and may annul citations',
  'role.CITIZEN': 'Citizen',

  'admin.users.create.cta': 'Create user',
  'admin.users.create.title': 'Create a user of the municipality',
  'admin.users.create.description':
    'The person is emailed a link to choose their own password. Until they do they cannot sign in, and nobody can sign in for them.',
  'admin.users.create.access.label': 'Access',
  'admin.users.create.access.description': 'What this person will be able to do, and where they are told about it.',
  'admin.users.create.roleLabel': 'Role',
  'admin.users.create.emailHint': 'The link to choose a password is sent to this address.',
  'admin.users.create.personalData.label': "The person's details",
  'admin.users.create.personalData.description':
    'The same details anyone gives when registering. Enter them as they appear in the file.',
  'admin.users.create.submit': 'Create and send the email',
  'admin.users.create.error.EMAIL_ALREADY_REGISTERED':
    'An account with that email already exists. Use \'Add staff member\' on the Staff screen: it looks the person up and gives them the post without creating them again.',
  'admin.users.create.error.DOCUMENT_ALREADY_REGISTERED':
    'An account with that identity document already exists. Use \'Add staff member\' on the Staff screen: it looks the person up and gives them the post without creating them again.',
  'admin.users.create.error.ROLE_NOT_ALLOWED_FOR_PORTAL':
    'A municipal administrator cannot grant that role. Ask the platform for it.',

  'admin.users.detail.grant.title': 'Grant access',
  'admin.users.detail.grant.description':
    'For somebody who already has an account: nothing is created, they are simply given access to this municipality.',
  'admin.users.detail.grant.roleLabel': 'Role',
  'admin.users.detail.grant.submit': 'Grant',
  'admin.users.detail.grant.error': 'The access could not be granted. They may already have it.',
  'admin.users.detail.actions.block': 'Block',
  'admin.users.detail.actions.unblock': 'Unblock',
  'admin.users.detail.actions.forcePasswordReset': 'Force password reset',
  'admin.users.detail.actions.assignRole': 'Assign role',
  'admin.users.detail.actions.approveMembership': 'Approve membership',
  'admin.users.detail.actions.rejectMembership': 'Reject membership',
  'admin.users.detail.blockReasonLabel': 'Block reason',
  'admin.users.detail.rejectReasonLabel': 'Rejection reason',
  'admin.users.detail.actionSuccess': 'The action was applied successfully.',
  'admin.users.detail.account.title': 'Account status',
  'admin.users.detail.account.description':
    "This person's LuParX account. There is one, and it belongs to no municipality.",
  'admin.users.detail.access.title': 'Municipal access',
  'admin.users.detail.access.description':
    'Every municipality where this person holds a post, with its role and status. These are DIFFERENT states from the account: deactivating a post does not block the account, and blocking the account revokes no post.',
  'admin.users.detail.access.none': 'This person holds no municipal access.',
  'admin.users.detail.access.role': 'Role',
  'admin.users.detail.access.status': 'Access status',
  'admin.users.detail.actions.forcePasswordReset.self':
    'You cannot force a password change on your own account: it would end your session.',
  'admin.users.detail.actions.failed': 'The action could not be applied.',
  'admin.users.status.ACTIVE': 'Active',
  'admin.users.status.BLOCKED': 'Blocked',
  'admin.users.status.PENDING_VERIFICATION': 'Pending verification',

  'admin.audit.title': 'Audit log',
  'admin.audit.column.actor': 'Actor',
  'admin.audit.column.action': 'Action',
  'admin.audit.column.resource': 'Resource',
  'admin.audit.column.occurredAt': 'Date',
  'admin.audit.column.origin': 'Origin',
  'admin.audit.actor.system': 'System',
  'admin.audit.actor.inactive': 'inactive',
  'admin.audit.origin.system': 'System process',
  'admin.audit.origin.fingerprint': 'Fingerprint of the connection. Not the address: the platform does not keep it.',
  'admin.audit.origin.probe.title': 'Check an IP address',
  'admin.audit.origin.probe.description':
    'The platform keeps a fingerprint, never an address. If you already have an address under suspicion, type it here and we will say whether it appears in this trail. The address is neither stored nor placed in the URL, and the check itself is recorded.',
  'admin.audit.origin.probe.placeholder': 'IP address',
  'admin.audit.origin.probe.submit': 'Check',
  'admin.audit.origin.probe.found': '{{count}} entry(ies) from that address. Fingerprint {{fingerprint}}.',
  'admin.audit.origin.probe.none': 'No entry of this municipality came from that address in the period queried.',
  'admin.audit.origin.probe.filter': 'Show only those entries',
  'admin.audit.origin.probe.failed': 'The address could not be checked.',
  'admin.audit.group.people': 'People and access',
  'admin.audit.group.staff': 'Staff and roles',
  'admin.audit.group.tenant': 'Municipality',
  'admin.audit.group.parking': 'Parking and bays',
  'admin.audit.group.exemptions': 'Exemptions',
  'admin.audit.group.citations': 'Citations',
  'admin.audit.group.finance': 'Finance and reconciliation',
  'admin.audit.group.security': 'Security and audit',
  'admin.audit.origin.probe.invalid':
    'That is not an IP address. Enter an IPv4 such as 190.10.1.25 or an IPv6 such as 2001:db8::1.',
  'admin.audit.filter.only': 'Show only this person',
  'admin.audit.filter.onlyPerson': 'Showing only what {{name}} did.',
  'admin.audit.filter.onlyOrigin': 'Showing only what came from connection {{fingerprint}}.',
  'admin.audit.filter.clear': 'Clear the filter',
  'admin.audit.column.changes': 'What changed',
  'admin.audit.masked': 'masked',
  'admin.audit.chain.title': 'Trail integrity',
  'admin.audit.chain.description':
    'Entries cannot be altered or deleted: the database refuses it. They are also sealed into a chain, so that if somebody managed it, it can be proven.',
  'admin.audit.chain.intact': 'Chain intact: {{entries}} entry(ies) covered by {{seals}} seal(s).',
  'admin.audit.chain.broken': 'The chain does not add up: {{problems}} finding(s). This needs a person to look at it.',
  'admin.audit.chain.sealedThrough': 'Sealed through {{date}}. Anything later is protected but not yet sealed.',
  'admin.audit.chain.notSealedYet': 'No seals yet: they are issued every ten minutes.',
  'admin.audit.chain.lastSeal': 'Last seal #{{seq}} · {{digest}}…',
  'admin.audit.chain.problem.DIGEST_MISMATCH': 'Rows changed or disappeared inside a window already sealed',
  'admin.audit.chain.problem.BROKEN_LINK': 'A seal does not follow the one before it',
  'admin.audit.chain.problem.MISSING_SEAL': 'A seal is missing from the series',
  'audit.action.USER_REGISTERED': 'Person registered',
  'audit.action.USER_CREATED': 'Account opened by an operator',
  'audit.action.USER_UPDATED': "Person's details updated",
  'audit.action.USER_BLOCKED': 'Account blocked',
  'audit.action.USER_UNBLOCKED': 'Account unblocked',
  'audit.action.USER_PASSWORD_RESET_REQUESTED': 'Password reset requested',
  'audit.action.USER_PASSWORD_CHANGED': 'Password changed',
  'audit.action.USER_EMAIL_VERIFIED': 'Email verified',
  'audit.action.USER_EMAIL_CHANGE_REQUESTED': 'Email change requested',
  'audit.action.USER_EMAIL_CHANGED': 'Email changed',
  'audit.action.USER_DIRECTORY_LOOKUP': 'People directory searched',
  'audit.action.LOGIN_SUCCEEDED': 'Sign-in succeeded',
  'audit.action.LOGIN_FAILED': 'Sign-in attempt failed',
  'audit.action.LOGOUT': 'Signed out',
  'audit.action.REFRESH_TOKEN_REUSE_DETECTED': 'Token reuse detected',
  'audit.action.SESSION_TENANT_SWITCHED': 'Active municipality switched',
  'audit.action.FEDERATED_IDENTITY_LINKED': 'Federated identity linked',
  'audit.action.MEMBERSHIP_REQUESTED': 'Access requested',
  'audit.action.MEMBERSHIP_CREATED': 'Post granted',
  'audit.action.MEMBERSHIP_APPROVED': 'Access approved',
  'audit.action.MEMBERSHIP_REJECTED': 'Access rejected',
  'audit.action.MEMBERSHIP_REVOKED': 'Officer revoked',
  'audit.action.MEMBERSHIP_SUSPENDED': 'Officer deactivated',
  'audit.action.MEMBERSHIP_REACTIVATED': 'Officer reactivated',
  'audit.action.MEMBERSHIP_ZONES_ASSIGNED': 'Sectors assigned',
  'audit.action.MEMBERSHIP_ROLE_CHANGED': 'Role changed',
  'audit.action.STAFF_INVITATION_SENT': 'Invitation sent',
  'audit.action.STAFF_INVITATION_REVOKED': 'Invitation revoked',
  'audit.action.STAFF_INVITATION_ACCEPTED': 'Invitation accepted',
  'audit.action.TENANT_CREATED': 'Municipality created',
  'audit.action.TENANT_UPDATED': 'Municipality updated',
  'audit.action.TENANT_STATUS_CHANGED': 'Municipality status changed',
  'audit.action.TENANT_SETTING_CHANGED': 'Municipality setting changed',
  'audit.action.TENANT_LOCALES_UPDATED': 'Municipality languages updated',
  'audit.action.TENANT_BRANDING_UPDATED': 'Visual identity updated',
  'audit.action.EXPORT_REQUESTED': 'Export requested',
  'audit.action.CATALOG_UPDATED': 'Catalogue updated',
  'audit.action.PLATFORM_SCOPE_ACCESS': 'Platform-scoped access',
  'audit.action.RETENTION_PURGE_RAN': 'Retention purge ran',
  'audit.action.AUDIT_ORIGIN_PROBED': 'IP address checked',
  'audit.action.VEHICLE_REGISTERED': 'Vehicle registered',
  'audit.action.VEHICLE_UPDATED': 'Vehicle updated',
  'audit.action.VEHICLE_DELETED': 'Vehicle deleted',
  'audit.action.VEHICLE_PRIMARY_CHANGED': 'Primary vehicle changed',
  'audit.action.PARKING_SESSION_STARTED': 'Stay started',
  'audit.action.PARKING_SESSION_EXTENDED': 'Stay extended',
  'audit.action.PARKING_SESSION_FINISHED': 'Stay finished',
  'audit.action.PARKING_POLICY_UPDATED': 'Parking policy updated',
  'audit.action.PARKING_SCHEDULE_UPDATED': 'Charging hours updated',
  'audit.action.PARKING_ZONE_CREATED': 'Zone created',
  'audit.action.PARKING_ZONE_UPDATED': 'Zone updated',
  'audit.action.PARKING_ZONE_RULES_UPDATED': 'Zone rules updated',
  'audit.action.PARKING_ZONE_GEOMETRY_UPDATED': 'Zone perimeter redrawn',
  'audit.action.PARKING_ZONE_GEOMETRY_CLEARED': 'Zone perimeter cleared',
  'audit.action.PARKING_ZONE_GEOMETRY_VERIFIED': 'Zone perimeter approved',
  'audit.action.PARKING_ZONE_GEOMETRY_REJECTED': 'Zone perimeter rejected',
  'audit.action.PARKING_RATE_UPDATED': 'Rate updated',
  'audit.action.PARKING_SPACE_CREATED': 'Bay created',
  'audit.action.PARKING_SPACE_UPDATED': 'Bay updated',
  'audit.action.PARKING_SPACE_RENAMED': 'Bay renamed',
  'audit.action.PARKING_SPACE_FORMAT_UPDATED': 'Bay code format updated',
  'audit.action.PLATE_EXEMPTION_REQUESTED': 'Exemption requested',
  'audit.action.PLATE_EXEMPTION_GRANTED': 'Exemption granted',
  'audit.action.PLATE_EXEMPTION_APPROVED': 'Exemption approved',
  'audit.action.PLATE_EXEMPTION_REJECTED': 'Exemption rejected',
  'audit.action.PLATE_EXEMPTION_AMENDED': 'Exemption amended',
  'audit.action.PLATE_EXEMPTION_REVOKED': 'Exemption revoked',
  'audit.action.PLATE_EXEMPTION_PLATE_ADDED': 'Plate added to an exemption',
  'audit.action.PLATE_EXEMPTION_PLATE_REMOVED': 'Plate removed from an exemption',
  'audit.action.PLATE_EXEMPTION_DOCUMENT_ATTACHED': 'Document attached to an exemption',
  'audit.action.EXEMPTION_TYPE_CREATED': 'Exemption type created',
  'audit.action.EXEMPTION_TYPE_UPDATED': 'Exemption type updated',
  'audit.action.CITATION_DRAFTED': 'Citation drafted',
  'audit.action.CITATION_ISSUED': 'Citation issued',
  'audit.action.CITATION_EVIDENCE_ATTACHED': 'Evidence attached to a citation',
  'audit.action.CITATION_STATUS_CHANGED': 'Citation status changed',
  'audit.action.CITATION_CANCELLED': 'Citation voided',
  'audit.action.CITATION_PAID': 'Citation paid',
  'audit.action.CITATION_INGESTED': 'Citation imported',
  'audit.action.INFRACTION_TYPES_UPDATED': 'Infraction types updated',
  'audit.action.EXTERNAL_CAUSAL_MAPPED': 'External cause mapped',
  'audit.action.ENFORCEMENT_SETTINGS_UPDATED': 'Enforcement settings updated',
  'audit.action.CITATION_APPEAL_FILED': 'Appeal filed',
  'audit.action.CITATION_APPEAL_RESOLVED': 'Appeal resolved',
  'audit.action.APPEAL_NOTICE_PUBLISHED': 'Appeal notice published',
  'audit.action.SETTLEMENT_IMPORTED': 'Settlement imported',
  'audit.action.SETTLEMENT_DISPUTED': 'Settlement disputed',
  'audit.action.WALLET_TOPUP_RECORDED': 'Wallet top-up recorded',
  'audit.action.WALLET_TOPUP_CODE_ROTATED': 'Top-up code rotated',
  'audit.action.WALLET_TOPUP_CODE_RESOLVED': 'Top-up code redeemed',
  'admin.audit.filter.action.all': 'All actions',
  'admin.audit.search.label': 'Search the trail',
  'admin.audit.search.placeholder': 'Search by resource id or event code',
  'admin.audit.pageSize.label': 'Rows per page',
  'admin.audit.pageSize.option': '{{count}} per page',
  'admin.audit.detail.open': 'See detail',
  'admin.audit.detail.title': 'Audit detail',
  'admin.audit.detail.actor': 'Actor',
  'admin.audit.detail.action': 'Action',
  'admin.audit.detail.module': 'Module / resource',
  'admin.audit.detail.changes': 'What changed',
  'admin.audit.detail.occurredAt': 'Date and time',
  'admin.audit.detail.origin': 'Origin',
  'admin.audit.detail.resourceId': 'Resource ID',
  'admin.audit.detail.technicalEvent': 'Technical event',
  'admin.audit.detail.fingerprint': 'Origin fingerprint',
  'admin.audit.field.name': 'Name',
  'admin.audit.field.description': 'Description',
  'admin.audit.field.active': 'Status',
  'admin.audit.field.code': 'Code',
  'admin.audit.field.divisionId': 'District',
  'admin.audit.value.active.true': 'Active',
  'admin.audit.value.active.false': 'Inactive',
  'admin.audit.changes.none': 'No data was changed',
  'admin.audit.changes.full': 'See the full value',
  'admin.audit.field.role': 'Role',
  'admin.audit.field.status': 'Status',
  'admin.audit.field.zones': 'Sectors',
  'admin.audit.field.prefix': 'Prefix',
  'admin.audit.field.digits': 'Character count',
  'admin.audit.field.allowLetters': 'Allow letters',
  'admin.audit.field.pattern': 'Validated pattern',
  'admin.audit.value.allowLetters.true': 'Yes',
  'admin.audit.value.allowLetters.false': 'No',
  'admin.audit.value.empty': '(empty)',
  'admin.audit.module.staff-invitation': 'Invitations',
  'admin.audit.module.wallet': 'Wallets',
  'admin.audit.module.parking-session': 'Stays',
  'admin.audit.module.vehicle': 'Vehicles',
  'admin.audit.module.tenant': 'Municipality',
  'admin.audit.module.audit': 'Audit trail',
  'admin.staff.revoke.title': "Revoke this person's post?",
  'admin.staff.revoke.body':
    '{{name}} loses this post in the municipality. Unlike deactivating, revoking is not lifted: giving the access back means granting the post again.',
  'admin.staff.revoke.keepsHistory':
    'The account is untouched and everything they did is kept. The citations they issued stay theirs.',
  'admin.staff.revoke.confirm': 'Revoke',
  'admin.staff.error.stale':
    'This list could not be refreshed, so it may be showing old data. Reload the screen.',
  'admin.staff.reset.title': 'Force a password change?',
  'admin.staff.reset.body':
    '{{name}} gets a link to set a new password. It does not change their post, role or sectors: to give back access that was taken away, use "Restore access".',
  'admin.staff.reset.closesSessions':
    'All their open sessions are closed, on every device. If they are working, they will have to sign in again.',
  'admin.staff.reset.confirm': 'Send the link',
  'admin.staff.error.selfAccount':
    'You cannot run this on your own account: it would sign you out immediately. Ask another administrator.',
  'admin.staff.error.self':
    'You cannot end or pause your own post. Ask another administrator of this municipality.',
  'admin.staff.changeRole.noAlternatives':
    'There is no other role available for this post in this app. To give them a role in another app, add a new post.',
  'admin.reports.registeredUsers.groupByLabel': 'Group by',
  'admin.reports.registeredUsers.reportLabel': 'Report',
  'admin.audit.filter.action': 'Action',
  'admin.audit.filter.from': 'From',
  'admin.audit.filter.to': 'To',
  'admin.audit.empty': 'No audit events matched those criteria.',

  'admin.reports.registeredUsers.title': 'Registered users',
  'admin.reports.title': 'Reports',
  'admin.reports.subtitle': "This municipality's reports, by period.",
  'admin.reports.typeLabel': 'Report type',
  'admin.reports.type.registered-users': 'Registered users',
  'admin.reports.fromLabel': 'From',
  'admin.reports.toLabel': 'To',
  'admin.reports.rangeInvalid': 'The start date must come before the end date.',
  'admin.reports.comingSoon':
    'The other municipal reports — revenue, occupancy by zone, citations, exemptions and reconciliation — will appear here once the server exposes them. This screen is ready for them.',
  'admin.reports.registeredUsers.groupBy': 'Group by',
  'admin.reports.registeredUsers.groupBy.tenant': 'Municipality',
  'admin.reports.registeredUsers.groupBy.country': 'Country',
  'admin.reports.registeredUsers.groupBy.portal': 'Portal',
  'admin.reports.registeredUsers.groupBy.month': 'Month',
  'admin.reports.registeredUsers.export': 'Export CSV',
  'admin.reports.registeredUsers.column.group': 'Group',
  'admin.reports.registeredUsers.column.count': 'Count',
  'admin.reports.registeredUsers.empty': 'No data for these filters.',
  'admin.reports.registeredUsers.period': 'People registered between {{from}} and {{to}}.',

  'home.citizen.title': 'Home',
  'home.citizen.parkingStub.title': 'Parking meters',
  'home.citizen.parkingStub.description':
    'Soon you will be able to start and pay for parking sessions here.',

  'nav.home': 'Home',
  'nav.portal.admin': 'Municipality',
  'nav.group.operation': 'Operation',
  'nav.group.administration': 'Administration',
  'nav.profile': 'Profile',
  'nav.users': 'Users',
  'nav.staff': 'Staff',
  'nav.zones': 'Zones',
  'nav.zonesGeometry': 'Perimeters',
  'admin.zoneGeometry.title': 'Zone perimeters',
  'admin.zoneGeometry.description': 'Upload each zone\'s official polygon (GeoJSON WGS84) and verify it so citizens see it on the map.',
  'admin.zoneGeometry.selectZone': 'Zone',
  'admin.zoneGeometry.selectZonePlaceholder': 'Choose a zone…',
  'admin.zoneGeometry.current': 'This zone already has a perimeter loaded.',
  'admin.zoneGeometry.currentNone': 'This zone has no perimeter yet.',
  'admin.zoneGeometry.paste': 'Paste the perimeter GeoJSON (Polygon or MultiPolygon, lon/lat order)',
  'admin.zoneGeometry.validate': 'Validate',
  'admin.zoneGeometry.valid': 'Valid GeoJSON',
  'admin.zoneGeometry.rings': '{{count}} ring(s)',
  'admin.zoneGeometry.vertices': '{{count}} vertices',
  'admin.zoneGeometry.outsideCR': 'Warning: the bounding box falls outside Costa Rica. Check the order is [lon, lat], not [lat, lon].',
  'admin.zoneGeometry.save': 'Save perimeter',
  'admin.zoneGeometry.saved': 'Perimeter saved. It stays unverified until you approve it.',
  'admin.zoneGeometry.verify': 'Verify as official',
  'admin.zoneGeometry.verified': 'Perimeter verified: it is now official and citizens will see it on the map.',
  'admin.zoneGeometry.verifyHint': 'Verify an official perimeter only. A DEMO geometry cannot be approved.',
  'admin.zoneGeometry.error.generic': 'Could not save the perimeter.',
  'admin.zoneGeometry.error.verify': 'Could not verify the perimeter.',
  'nav.spaces': 'Bays',
  'nav.tariffs': 'Tariffs',
  'nav.parkingPolicy': 'Parking policy',

  // ---- Municipality: parking policy ------------------------------------------------------------

  'admin.policy.title': 'Parking policy',
  'admin.policy.description':
    'What a citizen may do with a stay in this municipality: how much time they can buy, whether they can extend it, whether unused minutes come back to them, and how long after expiry they are still not fineable.',
  'admin.policy.preview.title': 'What the citizen will see',
  'admin.policy.preview.description': 'These are the durations they will be offered when choosing how long to park.',
  'admin.policy.preview.none': 'With these numbers the citizen cannot choose any duration, so they could not park at all.',
  'admin.policy.session.title': 'Durations on sale',
  'admin.policy.session.description':
    'The options in the picker. The floor and ceiling of a stay come from this list too, so there is no way to offer something that is then refused.',
  'admin.policy.session.empty': 'No duration yet.',
  'admin.policy.session.range': 'With this list you sell from {{min}} up to {{max}} in one go.',
  'admin.policy.error.emptyList': 'With no duration at all a citizen could not park. Add at least one.',
  'admin.policy.minuteField': 'Minutes',
  'admin.policy.addOption': 'Add',
  'admin.policy.removeOption': 'Remove {{option}}',
  'admin.policy.removeConfirm.title': 'Remove {{option}}?',
  'admin.policy.removeConfirm.body':
    'It stops being offered to citizens. The change applies when you save this screen.',
  'admin.policy.removeConfirm.confirm': 'Remove',
  'admin.policy.extension.title': 'Extensions',
  'admin.policy.extension.description': 'Whether a citizen may add time to a stay that is already running, and how much.',
  'admin.policy.extension.enabled': 'Allow extending a stay',
  'admin.policy.extension.enabledHint': 'With this off, a stay simply expires and they must start a new one.',
  'admin.policy.extension.empty': 'No extension duration yet.',
  'common.duration.hoursWithMinutes': '{{hours}} ({{minutes}} min)',
  'common.unit.minutes': 'minutes',
  'common.unit.days': 'days',
  'admin.policy.field.extensionMaxTotal': 'Ceiling including extensions',
  'admin.policy.field.extensionMaxTotalHint': 'The stay plus everything added to it. It cannot be below the longest duration you sell.',
  'admin.policy.credit.title': 'Finishing early and saved minutes',
  'admin.policy.credit.description':
    'Whether a citizen may close a stay before it expires, and whether the unused time is kept for next time.',
  'admin.policy.credit.earlyFinish': 'Allow finishing early',
  'admin.policy.credit.earlyFinishHint': 'Closing the stay on leaving, without waiting for it to expire.',
  'admin.policy.credit.enabled': 'Keep the minutes they did not use',
  'admin.policy.credit.enabledHint': 'Without finishing early there are no minutes to keep, so this switches off with it.',
  'admin.policy.field.creditMin': 'Minimum to keep any',
  'admin.policy.field.creditMinHint': 'If less than this is left over, nothing is kept.',
  'admin.policy.field.creditExpiry': 'Expire after (days)',
  'admin.policy.field.creditExpiryHint': 'How long saved minutes last. 0 means they never expire.',
  'admin.policy.grace.title': 'Grace period',
  'admin.policy.grace.description': 'How long after a stay expires a citation still cannot be issued.',
  'admin.policy.field.grace': 'Grace period',
  'admin.policy.field.graceHint': 'In minutes. 0 means no grace.',
  'admin.policy.grace.notice':
    'An officer who checks a plate within {{minutes}} of expiry will see it as covered.',
  'admin.policy.summary.title': 'How it ends up',
  'admin.policy.confirm.title': 'Save the parking policy?',
  'admin.policy.confirm.body':
    'From this moment it changes what can be sold to a citizen across the whole municipality. ' +
    'Stays already running keep the rules they had.',
  'admin.policy.confirm.noChanges':
    'Nothing differs from what is already stored. You can save anyway, but nothing will change.',
  'admin.policy.confirm.extension': 'Extensions allowed',
  'admin.policy.confirm.extensionList': 'Extension durations',
  'admin.policy.confirm.extensionMax': 'Ceiling including extensions',
  'admin.policy.confirm.earlyFinish': 'Early finish allowed',
  'admin.policy.confirm.credit': 'Credits back on early finish',
  'admin.policy.confirm.creditMin': 'Minimum left to credit back',
  'admin.policy.confirm.creditExpiry': 'Credit expires in',
  'admin.policy.confirm.days.one': '{{count}} day',
  'admin.policy.confirm.days.other': '{{count}} days',
  'admin.policy.summary.session': 'Durations',
  'admin.policy.summary.extension': 'Extensions',
  'admin.policy.summary.credit': 'Saved minutes',
  'admin.policy.summary.creditOn': 'From {{min}}, expiring after {{days}} days',
  'admin.policy.summary.grace': 'Grace period',
  'admin.policy.summary.overlap': 'Shared bay',
  'admin.policy.summary.overlapOn': 'Allowed',
  'admin.policy.summary.off': 'Off',
  'admin.policy.overlap.title': 'Bays with more than one stay',
  'admin.policy.overlap.description': 'What happens when somebody arrives at a bay a previous stay never released.',
  'admin.policy.overlap.enabled': 'Allow paying for a bay that reads as taken',
  'admin.policy.overlap.enabledHint':
    'Somebody who pays for two hours and leaves after fifteen minutes without finishing leaves the bay free on the street and taken in the system.',
  'admin.policy.overlap.notice':
    'Each stay covers its own plate on that bay. An inspector looks up plate and bay, so neither vehicle is exposed to a non-payment citation.',
  'admin.policy.overlap.offNotice':
    'With this off the second citizen cannot pay for the bay: they park anyway, with nothing to show an inspector.',
  'admin.policy.error.generic': 'The policy could not be saved.',
  'admin.policy.error.extensionCeiling': 'The ceiling including extensions cannot be below {{max}}, the longest stay you sell.',
  'nav.dashboard': 'Dashboard',
  'admin.dashboard.filters.apply': 'Apply',
  'admin.dashboard.filters.presets': 'Period shortcuts',
  'admin.dashboard.filters.preset.hoy': 'Today',
  'admin.dashboard.filters.preset.ayer': 'Yesterday',
  'admin.dashboard.filters.preset.ultimos7': 'Last 7 days',
  'admin.dashboard.filters.preset.ultimos30': 'Last 30 days',
  'admin.dashboard.filters.preset.esteMes': 'This month',
  'admin.dashboard.filters.preset.mesAnterior': 'Last month',
  'admin.dashboard.filters.preset.personalizado': 'Custom',
  'admin.dashboard.kpi.netRevenueInPeriod': 'Revenue in period',
  'admin.dashboard.kpi.paidStaysInPeriod': 'Paid stays in period',
  'admin.dashboard.kpi.citationsInPeriod': 'Citations in period',
  'admin.dashboard.kpi.netRevenue': 'Net revenue',
  'admin.dashboard.kpi.paidStays': 'Paid stays',
  'admin.dashboard.kpi.occupancy': 'Current occupancy',
  'admin.dashboard.kpi.citations': 'Citations issued',
  'admin.dashboard.kpi.inPeriod': 'In the period',
  'admin.dashboard.kpi.rightNow': 'Right now',
  'admin.dashboard.occupancy.overBays': '{{active}} of {{bays}} bays',
  'admin.dashboard.noData': 'No data',
  'admin.dashboard.parking.expand': 'Show all statuses',
  'admin.dashboard.parking.collapse': 'Show less',
  'admin.dashboard.group.finance': 'Finance',
  'admin.dashboard.group.operation': 'Operation',
  'admin.dashboard.title': 'Municipal dashboard',
  'admin.dashboard.window': 'From {{from}} to {{to}}.',
  'admin.dashboard.empty': 'Nothing in the period.',
  'admin.dashboard.revenue.title': 'Revenue',
  'admin.dashboard.revenue.description': 'Collected in the period, and what no provider has confirmed.',
  'admin.dashboard.revenue.captured': 'Charged',
  'admin.dashboard.revenue.capturedCount': '{{count}} payment(s)',
  'admin.dashboard.revenue.net': 'Net',
  'admin.dashboard.revenue.unsettled': 'Unconfirmed',
  'admin.dashboard.transactions.title': 'Wallet movements',
  'admin.dashboard.transactions.description': 'Charges are shown with a negative sign and top-ups with a positive one.',
  'admin.dashboard.parking.title': 'Stays in the period',
  'admin.dashboard.parking.description': 'Stays in the period, by how they were paid.',
  'admin.dashboard.occupancy.title': 'Occupancy by zone',
  'admin.dashboard.occupancy.description': 'Current occupancy over bays in service.',
  'admin.dashboard.occupancy.asOf': 'As of {{time}} · current state, not the period.',
  'admin.dashboard.occupancy.active': 'running stays',
  'admin.dashboard.occupancy.unzoned': '{{count}} of them outside every zone.',
  'admin.dashboard.occupancy.noZones': 'This municipality has no zones configured.',
  'admin.dashboard.occupancy.noBays': 'This zone has no numbered bays, so it has no percentage. It is not 0%.',
  'admin.dashboard.checks.title': 'Enforcement checks',
  'admin.dashboard.checks.description': 'Plate lookups in the period, by what they concluded.',
  'admin.dashboard.citations.title': 'Citations',
  'admin.dashboard.citations.description': 'By the date they happened, not the date they were issued.',
  'admin.dashboard.exemptions.title': 'Exemptions',
  'admin.dashboard.exemptions.description': 'As they stand today, not for the period.',
  'admin.dashboard.inspectors.title': 'Activity by officer',
  'admin.dashboard.inspectors.description': 'Lookups and citations side by side, for the period.',
  'admin.dashboard.inspectors.none': 'No officer recorded activity in the period.',
  'admin.dashboard.inspectors.notice': 'Compare checks and citations to put each officer\u2019s enforcement activity in context.',
  'admin.dashboard.failures.title': 'Payment failures',
  'admin.dashboard.failures.description': 'Payment attempts declined during the period, grouped by the provider\u2019s code.',
  'admin.dashboard.failures.none': 'No payment failures were recorded in the period.',
  'admin.dashboard.failures.count': 'failed attempt(s), worth {{amount}}',
  'admin.dashboard.column.zone': 'Zone',
  'admin.dashboard.column.active': 'Running',
  'admin.dashboard.column.bays': 'Bays in service',
  'admin.dashboard.column.occupancy': 'Occupancy',
  'admin.dashboard.column.checks': 'Lookups',
  'admin.dashboard.column.citations': 'Citations',
  'admin.dashboard.column.lastCheck': 'Last lookup',
  'admin.dashboard.column.code': 'Code',
  'admin.dashboard.column.reason': 'Reason',
  'admin.dashboard.column.attempts': 'Attempts',
  'wallet.transaction.top_up': 'Top-ups',
  'wallet.transaction.session_charge': 'Stay charges',
  'wallet.transaction.extension_charge': 'Extension charges',
  'wallet.transaction.adjustment': 'Adjustments',
  'parking.payment.paid': 'Paid',
  'parking.payment.no_charge': 'No charge',
  'parking.payment.pending': 'In progress',
  'parking.payment.failed': 'Failed',
  'parking.payment.unknown': 'Not recorded',
  'nav.billing': 'Reconciliation',
  'admin.billing.title': 'Payment reconciliation',
  'admin.billing.totals.title': 'The period',
  'admin.billing.totals.description':
    'What was charged, what the provider confirmed and what is still unconfirmed. All three come from the same data, so they cannot contradict each other between screens.',
  'admin.billing.totals.captured': 'Charged',
  'admin.billing.totals.capturedCount': '{{count}} payment(s)',
  'admin.billing.totals.settled': 'Confirmed by the provider',
  'admin.billing.totals.owed': 'Unconfirmed',
  'admin.billing.totals.net': 'Net to the municipality',
  'admin.billing.totals.netHint': 'After fees',
  'admin.billing.totals.window': 'From {{from}} to {{to}}.',
  'admin.billing.totals.failed': '{{count}} failed attempt(s) in the period. They are kept: they are the answer to \u201cI paid and my balance did not go up\u201d.',
  'admin.billing.unsettled.title': 'Charged and unconfirmed',
  'admin.billing.unsettled.description':
    'Money the municipality charged that no provider statement has confirmed yet. Counter cash and adjustments are not here: nobody is going to report them in a statement, and listing them would be a permanent alarm about something that is not a problem.',
  'admin.billing.unsettled.none': 'Everything charged has been confirmed by its provider.',
  'admin.billing.state.nothingToReconcile':
    'Nothing to reconcile in this period: what was charged goes through no provider statement.',
  'admin.billing.state.noSettlements':
    'No provider statement has arrived for this period yet. {{amount}} charged are waiting for the first one.',
  'admin.billing.state.pending': '{{amount}} charged that no statement confirms yet.',
  'admin.billing.state.reconciled':
    'Everything reconcilable in this period is confirmed: {{amount}}.',
  'admin.billing.totals.notApplicable': 'Outside any statement',
  'admin.billing.totals.notApplicableHint': 'Counter cash and adjustments: no provider reports them',
  'admin.billing.totals.settlementsInPeriod': '{{count}} provider statement(s) cover this period.',
  'admin.billing.settlements.title': 'Statements received',
  'admin.billing.settlements.description':
    'What each provider says it settled. Stored as they arrived: if the header totals do not match their own lines, that is a finding and not something to fix here.',
  'admin.billing.settlements.none': 'No statement has been imported yet.',
  'admin.billing.findings.title': 'What did not add up',
  'admin.billing.findings.description':
    'Only the lines somebody needs to look at. The matched ones are not listed: a statement is thousands of lines of which four are wrong, and showing all of them hides those four.',
  'admin.billing.findings.none': 'Every line of this statement adds up.',
  'admin.billing.column.confirmedAt': 'Confirmed',
  'admin.billing.column.method': 'Method',
  'admin.billing.column.reference': 'Reference',
  'admin.billing.column.gross': 'Amount',
  'admin.billing.column.reconciliation': 'Reconciliation',
  'admin.billing.column.provider': 'Provider',
  'admin.billing.column.period': 'Period',
  'admin.billing.column.declaredNet': 'Declared net',
  'admin.billing.column.deposit': 'Deposit',
  'admin.billing.column.status': 'Status',
  'admin.billing.column.finding': 'Finding',
  'admin.billing.deposit.pending': 'Pending',
  'wallet.topup.source.citizen': 'Citizen top-up',
  'wallet.topup.source.municipal_counter': 'Municipal counter',
  'wallet.topup.source.partner': 'Affiliated shop',
  'wallet.topup.source.adjustment': 'Adjustment',
  'wallet.topup.source.dev': 'TEST MODE · simulated balance',
  'payment.method.card': 'Card',
  'payment.method.sinpe': 'SINPE',
  'payment.method.bank_transfer': 'Bank transfer',
  'payment.method.counter_cash': 'Municipal counter',
  'payment.method.partner': 'Partner network',
  'payment.method.adjustment': 'Adjustment',
  'payment.state.pending': 'In progress',
  'payment.state.authorized': 'Authorised',
  'payment.state.captured': 'Charged',
  'payment.state.failed': 'Failed',
  'payment.state.cancelled': 'Cancelled',
  'payment.state.refunded': 'Refunded',
  'payment.state.charged_back': 'Charged back',
  'payment.checkout.state.open': 'In progress',
  'payment.checkout.state.completed': 'Finished',
  'payment.checkout.state.expired': 'Expired',
  'payment.purpose.wallet_topup': 'Wallet top-up',
  'payment.purpose.fine': 'Fine',
  'payment.purpose.permit': 'Permit',
  'payment.purpose.other': 'Other',
  'payment.network.visa': 'Visa',
  'payment.network.mastercard': 'Mastercard',
  'payment.network.american_express': 'American Express',
  'payment.network.discover': 'Discover',
  'payment.network.other': 'Other network',
  'payment.network.unknown': 'Network not stated',
  'payment.reconciliation.pending': 'Unconfirmed',
  'payment.reconciliation.matched': 'Confirmed',
  'payment.reconciliation.missing_in_settlement': 'Missing from the statement',
  'payment.reconciliation.amount_mismatch': 'Different amount',
  'payment.reconciliation.not_applicable': 'Not applicable',
  'settlement.status.imported': 'Imported',
  'settlement.status.reconciled': 'Reconciled',
  'settlement.status.disputed': 'Disputed',
  'settlement.line.matched': 'Matches',
  'settlement.line.unknown_payment': 'Payment unknown here',
  'settlement.line.amount_mismatch': 'Different amount',
  'settlement.line.duplicate': 'Duplicated in the statement',
  'nav.audit': 'Audit log',
  'nav.reports': 'Reports',
  'nav.tenants': 'Municipalities',
  'nav.park': 'Park',
  'nav.vehicles': 'Vehicles',
  'nav.wallet': 'Wallet',
  'nav.more': 'More',
  // --- Enforcement "More" menu (24-09-2026) ------------------------------------------------------
  'inspector.more.profile': 'My profile',
  'inspector.more.language': 'Language',
  'inspector.more.connection': 'Connection',
  'inspector.more.connection.online': 'Online. Citations are sent straight away.',
  'inspector.more.connection.offline': 'Offline. What you file is saved and sent on its own.',
  'inspector.more.connection.synced': 'Up to date',
  'inspector.more.connection.waiting': 'Waiting',
  'inspector.more.connection.pending.one': '{{count}} pending',
  'inspector.more.connection.pending.other': '{{count}} pending',
  'inspector.more.help': 'Help',
  'inspector.more.help.meta': 'How to work a shift.',
  'inspector.more.about': 'About LuParX',
  'inspector.more.about.meta': 'Opens the official site.',
  // --- Help ---------------------------------------------------------------------------------------
  'inspector.help.offline.action': 'See connection',
  'inspector.help.lead': 'How can we help?',
  'inspector.help.entry.diag': 'Diagnostics',
  'inspector.help.entry.permissions': 'Device permissions',
  'inspector.help.entry.sync': 'Sync',
  'inspector.help.entry.report': 'Report a problem',
  'inspector.help.permissions.title': 'Device permissions',
  'inspector.help.permissions.lead':
    'The camera matters most: without it no evidence can be attached to a citation.',
  'inspector.help.permissions.none': 'This app asks for no other device permission.',
  'inspector.help.sync.title': 'Sync',
  'inspector.help.sync.state': 'State',
  'inspector.help.sync.pendingLabel': 'Operations waiting',
  'inspector.help.sync.lastLabel': 'Last sync',
  'inspector.help.sync.never': 'This device has not sent anything yet.',
  'inspector.help.sync.errorsLabel': 'Errors',
  'inspector.help.sync.noErrors': 'None.',
  'inspector.help.report.title': 'Report a problem',
  'inspector.help.report.noBackend':
    'Reports cannot be sent from the app yet: that channel does not exist. What you can do is copy the app state and pass it to whoever supports you.',
  'inspector.help.report.copy': 'Copy the state',
  'inspector.help.report.copied': 'Copied. Paste it into your message to support.',
  'inspector.help.report.failed': 'Could not copy. The state is below so you can read it.',

  /* --- Support centre, extended 12-10-2026 ---------------------------------------------------- */
  'inspector.help.entry.diag.meta': 'Camera, location, connection and storage',
  'inspector.help.entry.permissions.meta': 'What you granted the app on this device',
  'inspector.help.entry.sync.meta': 'What is waiting and what was already sent',
  'inspector.help.entry.report.meta': 'Copy the state to pass on to support',
  'inspector.help.entry.guides': 'How-to guides',
  'inspector.help.entry.guides.meta': 'How each task of the shift works',
  'inspector.help.entry.about': 'About LuParX',
  'inspector.help.entry.about.meta': 'Version, environment and build',
  'inspector.help.diag.tone.unavailable': 'not available',

  'inspector.help.diag.connection.checking': 'Checking…',
  'inspector.help.diag.connection.ok': 'The server is reachable ({{ms}} ms)',
  'inspector.help.diag.connection.slow': 'The server is answering slowly ({{ms}} ms)',
  'inspector.help.diag.connection.slowNote':
    'Citations still go out, but sending may look stuck. Move somewhere with better signal if you can.',
  'inspector.help.diag.connection.unreachable': 'There is a network, but the server cannot be reached',
  'inspector.help.diag.connection.unreachableNote':
    'Usually a wifi asking you to accept its terms, or an outage. Keep working: the citation is stored here and goes out on its own.',
  'inspector.help.diag.connection.timeout': 'The server did not answer in time',
  'inspector.help.diag.connection.timeoutNote':
    'This can be weak signal. Keep working: the citation is stored here and goes out when it improves.',
  'inspector.help.diag.connection.unauthorized': 'The server is reachable, but the session is not valid',
  'inspector.help.diag.connection.unauthorizedNote': 'The network is fine. Sign in again.',
  'inspector.help.diag.connection.retest': 'Check again',

  'inspector.help.diag.camera.deniedEvidence':
    'This blocks the test here; a citation photograph may still work, because the phone\'s own picker takes it.',
  'inspector.help.diag.camera.testCancelled': 'You cancelled the test. No photo was saved.',

  'inspector.help.diag.location.test': 'Test location',

  /* --- Phase 1 of the two-phase brief (13-10-2026) ---------------------------------------------- */
  'inspector.help.diag.location.check': 'Check location',
  'inspector.help.diag.location.checking': 'Checking…',
  'inspector.help.diag.location.lastCheck': 'Last check: {{cuando}} · {{resultado}}',
  'inspector.help.diag.location.neverChecked': 'Last check: not done yet.',
  'inspector.help.diag.location.result.ok': 'position obtained',
  'inspector.help.diag.location.result.denied': 'permission denied',
  'inspector.help.diag.location.result.timeout': 'did not answer in time',
  'inspector.help.diag.location.result.unavailable': 'not available on this device',
  'inspector.help.diag.location.denied': 'Location permission denied',
  'inspector.help.diag.location.deniedWhy':
    'Checking again cannot grant it: the browser does not ask again once denied. It is enabled in the device settings, and this check then detects it.',
  'inspector.help.permissions.howTo': 'How to enable permissions on this device',
  'inspector.help.permissions.recheckNote':
    'Re-reads the camera permission, attempts a real location reading, flushes the queue and asks the server about the session. The camera is tested with its own button.',
  'inspector.help.diag.location.openMaps': 'Open in Google Maps',
  'inspector.help.diag.location.openWaze': 'Open in Waze',
  'inspector.help.diag.location.fix': 'Last reading: {{lat}}, {{lon}}',
  'inspector.help.diag.location.accuracy': 'Approximate accuracy: {{m}} m',
  'inspector.help.diag.location.accuracyUnknown': 'The device did not report accuracy.',
  'inspector.help.diag.location.timeout': 'The position was not obtained in time',
  'inspector.help.diag.location.timeoutNote':
    'This happens indoors and between tall buildings. Try outdoors; the citation is issued without coordinates either way.',
  'inspector.help.diag.location.unavailable': 'This device offers no location',
  'inspector.help.diag.location.unavailableNote':
    'There is nothing to fix in settings. The citation is issued anyway and records that there were no coordinates.',

  'inspector.help.permissions.settings.title': 'If it is denied, change it in the device settings',
  'inspector.help.permissions.settings.ios':
    'iPhone or iPad, installed app: Settings -> find LuParX in the list -> Camera and Location.',
  'inspector.help.permissions.settings.safari':
    'iPad or iPhone in Safari: tap "aA" in the address bar -> Website Settings -> Camera and Location. Also Settings -> Safari -> Camera and Location.',
  'inspector.help.permissions.settings.chrome':
    'Chrome: click the padlock (or settings icon) to the left of the address → Location → Allow. Then come back here and tap "Check location".',
  'inspector.help.permissions.settings.edge':
    'Edge: click the padlock to the left of the address → Permissions for this site → Location → Allow. Then come back here and tap "Check location".',
  'inspector.help.permissions.settings.firefox':
    'Firefox: click the padlock to the left of the address → clear the block on "Access your location". Then come back here and tap "Check location".',
  'inspector.help.permissions.settings.safariMac':
    'Safari on Mac: Safari menu → Settings → Websites → Location → set "Allow" for staging.luparx.com. If the Mac has location turned off: System Settings → Privacy & Security → Location Services.',
  'inspector.help.permissions.settings.otro':
    'In this browser the permission is changed from the icon to the left of the address, under the site permissions. Then come back here and tap "Check location".',
  'inspector.help.diag.location.insecure': 'Not available without a secure connection',
  'inspector.help.diag.location.insecureNote':
    'Browsers only hand over location on secure pages (https). Open the https address and check again. This is not a permission or a device problem.',
  'inspector.help.diag.location.unsupported': 'This browser does not offer location',
  'inspector.help.diag.location.unsupportedNote':
    'This browser cannot read location from a page. A citation is still issued, without coordinates.',
  'inspector.help.permissions.settings.android':
    'Android: Settings -> Apps -> the browser or LuParX -> Permissions.',
  'inspector.help.permissions.recheck': 'Check again',
  'inspector.help.permissions.apiNote':
    'The browser does not always allow reading a permission exact state or asking again from the page: that is why the change is made in settings and this screen only re-reads it.',

  'inspector.help.diag.sync.nothingYet': 'Nothing waiting - this device has not sent anything yet',
  'inspector.help.sync.sentLabel': 'Sent and confirmed',
  'inspector.help.sync.sent.one': '{{count}} citation confirmed by the server',
  'inspector.help.sync.sent.other': '{{count}} citations confirmed by the server',
  'inspector.help.sync.noneSent': 'None yet.',
  'inspector.help.sync.nothingYetNote':
    'This is not the same as "all synced": this device has not sent any citation.',

  'inspector.help.guides.title': 'How-to guides',
  'inspector.help.guides.lead':
    'What the app does in each task. Your municipality procedure - when a citation applies and what deadlines run - is not here.',
  'inspector.help.guide.lookup.title': 'Look up a vehicle',
  'inspector.help.guide.lookup.s1': 'Enter the plate on the home screen and tap "Search".',
  'inspector.help.guide.lookup.s2':
    'Pick the zone and type the bay. Both are optional, but without the bay coverage cannot be confirmed.',
  'inspector.help.guide.lookup.s3':
    'The result says one of six things: paid and current, payment expired, paid for another bay, no current payment, exempt, or the bay is missing to answer.',
  'inspector.help.guide.lookup.s4':
    '"Bay missing" is not an app error: with several stays on the same plate, the platform refuses to guess which car is in front of you.',
  'inspector.help.guide.lookup.s5': 'The last lookup stays visible below, in case you need to read it again.',
  'inspector.help.guide.cite.title': 'Issue a citation',
  'inspector.help.guide.cite.s1': 'A citation has four steps: vehicle, infraction, evidence and review.',
  'inspector.help.guide.cite.s2':
    'The type is chosen under "infraction". Some types require a photograph and cannot be issued without one; the screen says so.',
  'inspector.help.guide.cite.s3':
    'If there is a location, it is attached on its own. If not, the citation is issued anyway and records that there were no coordinates.',
  'inspector.help.guide.cite.s4': 'The last step shows everything together before issuing. Nothing is sent until you confirm there.',
  'inspector.help.guide.cite.s5': 'Once issued, the citation number is assigned by the server, not by the phone.',
  'inspector.help.guide.photos.title': 'Photographs and evidence',
  'inspector.help.guide.photos.s1': 'Photos are added INSIDE a citation, at the evidence step. There is no separate gallery.',
  'inspector.help.guide.photos.s2': 'Up to six photographs per citation.',
  'inspector.help.guide.photos.s3': 'Each one up to 10 MB. A heavier photo is rejected by the phone before it is stored.',
  'inspector.help.guide.photos.s4':
    'Offline, photos are stored on the phone together with the citation and travel with it when it is sent.',
  'inspector.help.guide.offline.title': 'Working without signal',
  'inspector.help.guide.offline.s1':
    'You can look up, raise citations and take photos offline. The only thing that needs signal is checking a plate against the server.',
  'inspector.help.guide.offline.s2': 'Every citation raised offline is stored on this phone and appears under "Pending".',
  'inspector.help.guide.offline.s3': 'They are sent on their own when signal returns. You do not need to do anything.',
  'inspector.help.guide.offline.s4':
    'Closing the app does not clear the queue. What does put it at risk is the browser private mode or running out of space: the diagnostic checks for that.',
  'inspector.help.guide.sync.title': 'How citations are sent',
  'inspector.help.guide.sync.s1': 'A citation goes through four states: waiting, sending, sent, or failed.',
  'inspector.help.guide.sync.s2': '"Sync now" only brings forward what was going to go out anyway.',
  'inspector.help.guide.sync.s3': '"Retry" touches only the ones that failed. Those already sent are not sent again.',
  'inspector.help.guide.sync.s4':
    'Each citation travels with its own key, so a retry never creates a duplicate citation on the server.',
  'inspector.help.guide.trouble.title': 'If the camera or GPS does not respond',
  'inspector.help.guide.trouble.s1':
    'Open "Diagnostic": it shows the real state of each permission and offers a button where something can be done.',
  'inspector.help.guide.trouble.s2':
    'A denied permission cannot be fixed from the app. It is changed in the device settings; "Device permissions" explains where.',
  'inspector.help.guide.trouble.s3': 'After changing it in settings, come back and tap "Check again".',
  'inspector.help.guide.trouble.s4':
    'You can keep working without GPS: the citation is issued without coordinates and records the fact. A location is never invented.',

  'inspector.help.about.title': 'About LuParX',
  'inspector.help.about.version': 'Version',
  'inspector.help.about.build': 'Build',
  'inspector.help.about.environment': 'Environment',
  'inspector.help.about.builtAt': 'Built on',
  'inspector.help.about.unknown': 'Not available in this build',
  'inspector.help.about.note':
    'These values are written by the build process. When one says "not available", this build did not receive it - it is not replaced by an invented value.',
  'inspector.help.title': 'Help',
  'inspector.help.diag.title': 'App diagnostics',
  'inspector.help.diag.checking': 'Checking…',
  'inspector.help.diag.allGood': 'Everything is working correctly.',
  'inspector.help.diag.someWarning': 'It works, but there is something you should know.',
  'inspector.help.diag.someIssue': 'We found something that needs solving.',
  'inspector.help.diag.tone.ok': 'fine',
  'inspector.help.diag.tone.warning': 'heads up',
  'inspector.help.diag.tone.problem': 'problem',
  'inspector.help.diag.connection': 'Connection',
  'inspector.help.diag.connection.note':
    'Keep working: the citation is stored on the phone and sends itself when signal returns.',
  'inspector.help.diag.sync': 'Sync',
  'inspector.help.diag.sync.ok': 'Everything is synced',
  'inspector.help.diag.sync.pending.one': '{{count}} operation waiting',
  'inspector.help.diag.sync.pending.other': '{{count}} operations waiting',
  'inspector.help.diag.sync.failed.one': '{{count}} operation could not be sent',
  'inspector.help.diag.sync.failed.other': '{{count}} operations could not be sent',
  'inspector.help.diag.sync.note': 'They send themselves; this only brings the send forward.',
  'inspector.help.diag.sync.offlineNote': 'They cannot be sent without signal. They go out on their own when it returns.',
  'inspector.help.diag.sync.failedNote':
    'In Pending you can open each one and see why it failed. Retrying duplicates nothing: the same idempotency key is reused.',
  'inspector.help.diag.sync.action': 'Sync now',
  'inspector.help.diag.sync.retry': 'Retry now',
  'inspector.help.diag.sync.working': 'Sending…',
  'inspector.help.diag.session': 'Session',
  'inspector.help.diag.session.ok': 'Session active',
  'inspector.help.diag.session.problem': 'Session unavailable',
  'inspector.help.diag.session.note': 'Sign in again with your account to keep working.',
  'inspector.help.diag.session.action': 'Sign in',
  'inspector.help.diag.session.noTenant': 'No active municipality',
  'inspector.help.diag.session.noTenantNote':
    'Your session is open but no municipality has been chosen, so nothing can be checked or issued.',
  'inspector.help.diag.session.chooseTenant': 'Choose a municipality',
  'inspector.help.diag.location': 'Location',
  'inspector.help.diag.location.deniedNote':
    'The citation is issued all the same, without coordinates. Turn it on in the phone settings, under LuParX permissions.',
  'inspector.help.diag.permission.granted': 'Permission granted',
  'inspector.help.diag.permission.asks': 'Asked for when used',
  'inspector.help.diag.permission.denied': 'Permission denied',
  'inspector.help.diag.permission.working': 'Asking…',
  'inspector.help.diag.location.allow': 'Allow location',
  'inspector.help.diag.location.allowNote':
    'With permission granted, a plate check records where it was made. Without it the citation is issued all the same, with no coordinates.',
  'inspector.help.diag.app': 'App',
  'inspector.help.diag.app.ok': 'Working correctly',
  'inspector.help.diag.app.problem': 'This phone is not storing data',
  'inspector.help.diag.app.note':
    'A citation filed without signal could be lost when the app closes. It is usually the browser private mode or full storage.',
  'inspector.help.diag.app.reload': 'Reload the app',
  'citizen.more.profile': 'My profile',
  'citizen.more.fines': 'Fines',
  'citizen.more.fines.meta': 'Review and appeal your fines.',
  'citizen.more.language': 'Choose language',
  'citizen.more.notifications': 'Notifications',
  'citizen.more.notifications.meta': 'What happened, and what of it reaches your email.',
  // --- Citizen "More" menu: what the 24-09-2026 spec added ---------------------------------------
  'citizen.more.connection': 'Connection',
  'citizen.more.connection.online': 'Online. Everything is saved right away.',
  'citizen.more.connection.offline': 'Offline. Try again once you have signal.',
  'citizen.more.connection.ok': 'Online',
  'citizen.more.connection.none': 'Offline',
  'citizen.more.help': 'Help',
  'citizen.more.help.meta': 'How parking and citations work.',
  'citizen.more.about': 'About LuParX',
  'citizen.more.about.meta': 'Opens the official site.',
  // --- Citizen help ---------------------------------------------------------------------------------
  'citizen.help.title': 'Help',
  'citizen.help.ask': 'How can we help?',
  'citizen.help.search.label': 'Search help',
  'citizen.help.search.placeholder': 'What are you looking for?',
  'citizen.help.search.empty': 'Nothing found for "{{query}}". Try other words or pick a category.',
  'citizen.help.search.go': 'See how to solve it',
  'citizen.help.whatDoYouNeed': 'What do you need?',
  'citizen.help.whatToSolve': 'What do you need to solve?',
  'citizen.help.whatIsHappening': 'What is happening?',
  'citizen.help.cat.parking.title': 'My parking',
  'citizen.help.cat.payments.title': 'Payments and wallet',
  'citizen.help.cat.fines.title': 'Fines and citations',
  'citizen.help.cat.vehicles.title': 'Vehicles',
  'citizen.help.cat.app.title': 'App problems',
  'citizen.help.status.title': 'App status',
  'citizen.help.status.body': 'Check the connection, your session and whether this device is storing data.',
  'citizen.help.status.action': 'See diagnostics',
  'citizen.help.opt.parking.moreTime.title': 'I need more time',
  'citizen.help.opt.parking.moreTime.body':
    'You can extend from the home screen while the stay is still active. Once it has ended it cannot be extended: you start a new one, and in between the car is uncovered.',
  'citizen.help.opt.parking.moreTime.action': 'See the active stay',
  'citizen.help.opt.parking.cantStart.title': 'I cannot start my parking',
  'citizen.help.opt.parking.cantStart.body':
    'Check that the vehicle is registered, that there is balance and that the bay exists in the zone. The screen says which of the five steps is missing.',
  'citizen.help.opt.parking.cantStart.action': 'Park',
  'citizen.help.opt.parking.ended.title': 'My parking has ended',
  'citizen.help.opt.parking.ended.body':
    'An expired stay cannot be reopened. If the car is still there, start a new one: with no active stay the bay reads as unpaid.',
  'citizen.help.opt.parking.ended.action': 'Park again',
  'citizen.help.opt.parking.cantPay.title': 'I cannot pay',
  'citizen.help.opt.parking.cantPay.body':
    'Parking is charged to the wallet. If the balance is short, top it up first and try again.',
  'citizen.help.opt.parking.cantPay.action': 'See wallet',
  'citizen.help.opt.parking.location.title': 'I have a problem with my location',
  'citizen.help.opt.parking.location.body':
    'GPS is not needed: the zone and the bay are picked from a list, and the bay number is what identifies where the car is.',
  'citizen.help.opt.parking.location.action': 'Park',
  'citizen.help.opt.payments.cantPay.title': 'I cannot make a payment',
  'citizen.help.opt.payments.cantPay.body':
    'Check the wallet balance. If the charge was declined, the movement is recorded with the reason.',
  'citizen.help.opt.payments.cantPay.action': 'See wallet',
  'citizen.help.opt.payments.missing.title': 'My payment is missing',
  'citizen.help.opt.payments.missing.body':
    'Every charge and every top-up leaves a movement with its date and reference. If it is not there, it was not recorded.',
  'citizen.help.opt.payments.missing.action': 'See movements',
  'citizen.help.opt.payments.movements.title': 'I want to review my movements',
  'citizen.help.opt.payments.movements.body':
    'Every entry and exit from the wallet in detail, newest first.',
  'citizen.help.opt.payments.movements.action': 'See movements',
  'citizen.help.opt.payments.unknownCharge.title': 'I do not recognise a charge',
  'citizen.help.opt.payments.unknownCharge.body':
    'Find the movement by its date: it says what caused it. If the plate is shared, someone else may have used it.',
  'citizen.help.opt.payments.unknownCharge.action': 'See movements',
  'citizen.help.opt.payments.walletHelp.title': 'I need help with my wallet',
  'citizen.help.opt.payments.walletHelp.body':
    'It is topped up at the enabled points or by card. The balance is what pays for stays.',
  'citizen.help.opt.payments.walletHelp.action': 'See wallet',
  'citizen.help.opt.fines.got.title': 'I received a citation',
  'citizen.help.opt.fines.got.body':
    'It shows the reason, the zone and the time. It is a municipal act: it does not disappear on its own, but it can be appealed.',
  'citizen.help.opt.fines.got.action': 'See fines',
  'citizen.help.opt.fines.appeal.title': 'I want to appeal a citation',
  'citizen.help.opt.fines.appeal.body':
    'You appeal from the citation detail, telling what happened. Meanwhile the citation stands, so mind the deadline.',
  'citizen.help.opt.fines.appeal.action': 'Pick the citation',
  'citizen.help.opt.fines.dontUnderstand.title': 'I do not understand a citation',
  'citizen.help.opt.fines.dontUnderstand.body':
    'The detail carries the infraction, where and when it was, and the evidence attached when it was issued.',
  'citizen.help.opt.fines.dontUnderstand.action': 'See the detail',
  'citizen.help.opt.fines.mine.title': 'Check my citations',
  'citizen.help.opt.fines.mine.body': 'Every citation on your plates, paid and outstanding.',
  'citizen.help.opt.fines.mine.action': 'See fines',
  'citizen.help.opt.vehicles.add.title': 'Add a vehicle',
  'citizen.help.opt.vehicles.add.body':
    'You can only park with a registered plate. You add it once and it stays available.',
  'citizen.help.opt.vehicles.add.action': 'See vehicles',
  'citizen.help.opt.vehicles.edit.title': 'Change a vehicle',
  'citizen.help.opt.vehicles.edit.body':
    'The name you recognise it by can be corrected. The plate itself is not edited: add the right one and remove the other.',
  'citizen.help.opt.vehicles.edit.action': 'See vehicles',
  'citizen.help.opt.vehicles.list.title': 'Check my vehicles',
  'citizen.help.opt.vehicles.list.body': 'The plates registered to you in this municipality.',
  'citizen.help.opt.vehicles.list.action': 'See vehicles',
  'citizen.help.opt.vehicles.shared.title': 'Shared plates',
  'citizen.help.opt.vehicles.shared.body':
    'One plate can be registered by more than one person. When that happens the citation is seen, paid and appealed from both accounts, and whoever pays first pays for everyone.',
  'citizen.help.opt.vehicles.shared.action': 'See vehicles',
  'citizen.help.opt.app.offline.title': 'I have no connection',
  'citizen.help.opt.app.offline.body':
    'Without a connection a stay cannot be started or paid: everything is confirmed against the server at the time.',
  'citizen.help.opt.app.offline.action': 'See diagnostics',
  'citizen.help.opt.app.location.title': 'I have a problem with my location',
  'citizen.help.opt.app.location.body':
    'The app does not need your location for anything: the zone and the bay are picked by hand.',
  'citizen.help.opt.app.location.action': 'See diagnostics',
  'citizen.help.opt.app.notifications.title': 'I am not getting notifications',
  'citizen.help.opt.app.notifications.body':
    'Notices always arrive on that screen, and by email according to what you chose in its preferences.',
  'citizen.help.opt.app.notifications.action': 'See notices',
  'citizen.help.opt.app.error.title': 'The app shows an error',
  'citizen.help.opt.app.error.body':
    'Reloading mounts it again from scratch and clears almost every screen error. It deletes nothing of yours.',
  'citizen.help.opt.app.error.action': 'Reload',
  'citizen.help.opt.app.diagnostic.title': 'App diagnostics',
  'citizen.help.opt.app.diagnostic.body':
    'Checks the connection, your session and whether this device is storing data, all at once.',
  'citizen.help.opt.app.diagnostic.action': 'See diagnostics',
  'citizen.help.diag.title': 'App diagnostics',
  'citizen.help.diag.allGood': 'Everything is working correctly.',
  'citizen.help.diag.someWarning': 'It works, but there is something you should know.',
  'citizen.help.diag.someIssue': 'We found something that needs solving.',
  'citizen.help.permissions.title': 'Device permissions',
  'citizen.help.permissions.body': 'Check whether the camera is enabled for taking evidence photographs.',
  'citizen.help.permissions.action': 'Open permissions',
  'citizen.help.permissions.lead': 'The camera is used to attach photographs when you appeal a fine. The browser asks for it at the moment of use.',
  'citizen.help.permissions.ready': 'The camera answered: it is ready to use.',
  'citizen.help.permissions.pending': 'The camera has not been checked on this device yet.',
  'citizen.help.permissions.none': 'This application asks for no other device permission. Location is not used: the zone is chosen from a list.',

  'citizen.help.diag.tone.ok': 'fine',
  'citizen.help.diag.tone.warning': 'heads up',
  'citizen.help.diag.tone.unavailable': 'not available',
  'citizen.help.diag.tone.problem': 'problem',
  'citizen.help.diag.connection': 'Connection',
  'citizen.help.diag.connection.ok': 'Online',
  'citizen.help.diag.connection.off': 'No connection',
  'citizen.help.diag.connection.note':
    'Without a connection a stay cannot be started or paid. Check your data or wifi and try again.',
  'citizen.help.diag.session': 'Session',
  'citizen.help.diag.session.ok': 'Session active',
  'citizen.help.diag.session.problem': 'Session unavailable',
  'citizen.help.diag.session.note': 'Sign in again with your account to keep using the app.',
  'citizen.help.diag.app': 'App',
  'citizen.help.diag.app.ok': 'Working correctly',
  'citizen.help.diag.app.problem': 'This device is not storing data',
  'citizen.help.diag.app.note':
    'The app will ask you to sign in every time. It is usually the browser private mode or full storage.',
  'citizen.help.diag.app.reload': 'Reload the app',

  'citizen.reminder.expiring.title': '{{minutes}} min of parking left',
  'citizen.reminder.expiring.body': '{{plate}} in space {{space}}. Extend from the app if you need to.',
  'citizen.reminder.expired.title': 'Your parking has expired',
  'citizen.reminder.expired.body': '{{plate}} in space {{space}}.',
  'citizen.reminder.permission.title': 'Want a warning before it runs out?',
  'citizen.reminder.permission.body': 'Your phone warns you even with no signal. You can turn it off whenever you like.',
  'citizen.reminder.permission.allow': 'Warn me',
  'citizen.reminder.permission.dismiss': 'Not now',
  'citizen.notifications.title': 'Notifications',
  'citizen.notifications.subtitle': 'What happened in this municipality.',
  'citizen.notifications.markAllRead': 'Mark all as read',
  'citizen.notifications.unread': 'Unread',
  'citizen.notifications.empty.title': 'All caught up',
  'citizen.notifications.empty.description': 'There are no notifications in this municipality.',
  'citizen.notifications.preferences.open': 'Notification preferences',
  'citizen.notifications.preferences.title': 'Email alerts',
  'citizen.notifications.preferences.description': 'The bell shows everything. Choose what also reaches your email.',
  'citizen.notifications.preferences.emailEnabled': 'Also tell me by email',
  'citizen.notifications.preferences.emailEnabledHint': 'Sent to the address you sign in with. You can turn it off whenever you like.',
  'citizen.notifications.preferences.categories': 'What should we tell you about?',
  'citizen.notifications.preferences.error': 'The preference could not be saved.',

  'notification.category.PARKING': 'Parking',
  'notification.category.FINES': 'Fines',
  'notification.category.WALLET': 'Wallet and minutes',

  'notification.type.PARKING_SESSION_EXPIRING': 'Your parking for plate {{plate}} runs out at {{when}}.',
  'notification.type.PARKING_SESSION_EXPIRED': 'The parking for plate {{plate}} ran out at {{when}}.',
  'notification.type.CITATION_ISSUED': 'Citation {{citationNumber}} was issued against you, for {{amount}}.',
  'notification.type.APPEAL_RESOLVED': 'The municipality has resolved your defence against citation {{citationNumber}}.',
  'notification.type.WALLET_TOPUP_CREDITED': '{{amount}} was credited to your wallet.',
  'notification.type.TIME_CREDITS_EXPIRING': 'Your {{minutes}} saved minutes lapse on {{when}}.',
  'notification.type.CITATION_VOIDED': 'Citation {{citationNumber}} for plate {{plate}} was annulled.',
  'notification.type.CITATION_VOIDED.reason': 'Reason: {{reason}}',
  'notification.type.POST_SUSPENDED': 'Your access to this municipality was deactivated.',
  'notification.type.POST_SUSPENDED.reason': 'Reason: {{reason}}',
  'notification.type.POST_REACTIVATED': 'Your access to this municipality was restored.',
  'notification.type.POST_ROLE_CHANGED': 'Your role changed from {{previousRole}} to {{role}}.',
  'notification.type.POST_ZONES_CHANGED.one': 'Your sectors were reassigned: you now cover 1.',
  'notification.type.POST_ZONES_CHANGED.other': 'Your sectors were reassigned: you now cover {{count}}.',
  'notification.type.POST_ZONES_CHANGED.all': 'Your sectors were reassigned: you may now work in all of them.',
  'inspector.notifications.title': 'Notices',
  'inspector.notifications.subtitle': 'What happened with your work in this municipality.',
  'inspector.notifications.open': 'Notices',
  'inspector.notifications.markAllRead': 'Mark all as read',
  'inspector.notifications.unread': 'Unread',
  'inspector.notifications.empty.title': 'Nothing new',
  'inspector.notifications.empty.description': 'This is where an annulled citation or a change to your post shows up.',
  'inspector.notifications.queue.title': 'Citations waiting to upload',
  'inspector.notifications.queue.one': '1 citation has not left the phone yet.',
  'inspector.notifications.queue.other': '{{count}} citations have not left the phone yet.',
  'inspector.notifications.queue.action': 'See pending',

  'nav.fines': 'Fines',
  'nav.catalogs': 'Catalogs',
  'nav.system': 'System',

  'profile.title': 'My profile',
  'profile.changeTenant': 'Change active municipality',
  'profile.verifiedBadge': 'Verified',
  'profile.activeTenant': 'Active municipality',

  'common.notifications': 'Notifications',
  'common.viewAll': 'View all',
  'common.today': 'Today',

  // ---- Citizen app screens (mocked domain — module-parking not shipped yet) --------------------

  'locale.name.es-CR': 'Spanish',
  'locale.name.en-US': 'English',

  'citizen.home.greeting': 'Hi, {{name}} 👋',
  'citizen.home.subtitle.noSession': 'Park easily and quickly.',
  'citizen.home.subtitle.activeSession': 'Your parking session is active.',
  'citizen.home.subtitle.expiredSession': 'Your parking session has expired.',
  'citizen.home.cta.title': 'Park now',
  'citizen.home.cta.subtitle': 'Choose a zone, vehicle and time',
  'citizen.home.balanceCard.action': 'Top up',
  'citizen.home.vehicleCard.label': 'Primary vehicle',
  'citizen.home.vehicleCard.changeCta': 'Change',
  'citizen.home.vehicleCard.empty': 'No vehicles',
  'citizen.home.vehicleCard.addCta': 'Add',
  'citizen.home.balanceCard.empty': 'No balance',
  'citizen.home.finesCard.label': 'Pending fines',
  'citizen.home.finesCard.none': 'None',
  'citizen.home.finesCard.one': '1 fine to pay',
  'citizen.home.finesCard.many': '{{count}} fines to pay',
  'citizen.home.finesCard.amount': 'Total: {{amount}}',
  'citizen.home.finesCard.loading': 'Checking…',
  'citizen.home.activity.title': 'Recent activity',
  'citizen.home.activity.viewAllCta': 'View all',
  'citizen.home.activeSession.title': 'Active parking session',
  'citizen.home.activeSession.titleExpired': 'Expired parking session',
  'citizen.home.activeSession.expired.withinGrace':
    'It expired. This municipality allows {{minutes}} of grace; after that a citation can be issued.',
  'citizen.home.activeSession.expired.graceOver': 'It expired and a citation can be issued.',
  'citizen.home.activeSession.statusActive': 'Active',
  'citizen.home.activeSession.statusExpired': 'Expired',
  'citizen.home.activeSession.urgency.warning': 'Less than 10 minutes left.',
  'citizen.home.activeSession.urgency.critical': 'Less than 5 minutes left: extend or head back.',
  'citizen.timer.expired': 'Expired',
  'citizen.home.activeSession.zoneAndSpace': 'Zone: {{zone}} · Space {{space}}',
  'citizen.home.activeSession.remainingLabel': 'time remaining',
  'citizen.home.activeSession.expiresAt': 'Expires today at {{time}}',
  'citizen.home.activeSession.expiredAt': 'Expired today at {{time}}',
  'citizen.home.activeSession.extendCta': 'Extend time',
  'citizen.home.activeSession.finishCta': 'Finish now',

  'citizen.timerBar.moreCount': '+{{count}}',
  'citizen.timerBar.moreLabel': 'View all active sessions',
  'citizen.timerBar.listTitle': 'Active sessions',
  'citizen.timerBar.announce.warning': '{{plate}}: less than 10 minutes remaining.',
  'citizen.timerBar.announce.expired': '{{plate}}: parking session expired.',

  'citizen.parking.title': 'Park',
  'citizen.parking.subtitle': 'Follow these steps to start your session.',
  'citizen.parking.step0.title': 'Municipality',
  'citizen.parking.step1.title': 'Zone and space',
  'citizen.parking.step1.description': 'Select the zone where you are parked.',
  'citizen.parking.step1.zoneLabel': 'Zone',
  'citizen.parking.step1.viewMapCta': 'View map',
  'citizen.parking.step2.title': 'Vehicle',
  'citizen.parking.step2.selectLabel': 'Select a vehicle',
  'citizen.parking.step2.empty': 'Add a vehicle before parking.',
  'citizen.parking.step2.guestOption': 'Another vehicle',
  'citizen.parking.step2.guestOptionDetail': "Not yours · the plate isn't saved",
  'citizen.parking.step2.guestPlateLabel': 'Vehicle plate',
  'citizen.parking.step2.guestPlateHint':
    "Used for this stay only and kept on the receipt. It isn't added to your vehicles.",
  'citizen.parking.step2.guestPlatePlaceholder': 'BHL019',
  'citizen.parking.step2.guestTypeLabel': 'Vehicle type',
  'citizen.parking.step2.addVehicleCta': 'Add vehicle',
  'citizen.parking.step3.title': 'Time',
  'citizen.parking.step3.creditApplied': 'includes {{minutes}} of credit',
  'citizen.parking.step3.benefitHeading': 'Included benefit',
  'citizen.parking.step3.freeBadge': 'FREE',
  'citizen.parking.step3.noChargeBadge': 'No charge',
  'citizen.parking.step3.freeMinutesBenefit': '{{minutes}} free',
  'citizen.parking.step3.savedMinutes': 'Your saved minutes',
  'citizen.parking.step3.courtesy': 'Courtesy, once a day',
  'citizen.parking.step4.title': 'Summary and payment',
  'citizen.parking.step4.zoneLabel': 'Zone',
  'citizen.parking.step4.vehicleLabel': 'Vehicle',
  'citizen.parking.step4.durationLabel': 'Duration',
  'citizen.parking.step4.amountLabel': 'Amount',
  'citizen.parking.step4.creditAppliedLabel': 'Time credit applied',
  'citizen.parking.step4.balanceLabel': 'Available balance',
  'citizen.parking.step4.balanceAfterLabel': 'Balance after paying',
  'citizen.parking.falta.zona': 'Choose a zone to continue.',
  'citizen.parking.falta.espacio': 'Enter the bay code.',
  'citizen.parking.falta.vehiculo': 'Choose a vehicle.',
  'citizen.parking.falta.duracion': 'Choose how long you will park.',
  'citizen.parking.step4.shortBy': 'You are {{amount}} short for this stay.',
  'citizen.parking.step4.topUpCta': 'Top up and come back',
  'citizen.parking.step4.payableLabel': 'Payable from your wallet',
  'citizen.parking.submit': 'Start parking session',
  'citizen.parking.wizard.step1': 'Zone',
  'citizen.parking.wizard.step2': 'Vehicle',
  'citizen.parking.wizard.step3': 'Confirm',
  'citizen.parking.wizard.step4': 'Done',
  'citizen.parking.wizard.progress': 'Step {{current}} of {{total}}',
  'citizen.parking.wizard.continue': 'Continue',
  'citizen.parking.step4.confirmAndPay': 'Confirm and pay {{amount}}',
  'citizen.parking.result.title': 'All set!',
  'citizen.parking.result.subtitle': 'Your parking is active.',
  'citizen.parking.result.validUntil': 'Valid until',
  'citizen.parking.result.seeMine': 'See my parking',
  'citizen.parking.result.extendCta': 'Extend time',
  'citizen.parking.result.seeOnMap': 'See on the map',
  'citizen.parking.result.hideMap': 'Hide the map',
  'citizen.parking.durationMinutes.one': '{{count}} minute',
  'citizen.parking.durationMinutes.other': '{{count}} minutes',
  'citizen.parking.durationHours.one': '{{count}} hour',
  'citizen.parking.durationHours.other': '{{count}} hours',

  'citizen.parking.extend.title': 'Extend parking',
  'citizen.parking.extend.currentlyEndsAt': 'It currently ends at {{time}}.',
  'citizen.parking.extend.chooseLabel': 'Additional time',
  'citizen.parking.extend.chargeableMinutes': 'Only {{minutes}} are charged; the rest falls outside charging hours.',
  'citizen.parking.extend.creditApplied': 'Includes {{minutes}} of credit.',
  'citizen.parking.extend.costLabel': 'Extension price',
  'citizen.parking.extend.newExpiryLabel': 'New expiry time',
  'citizen.parking.extend.walletNote': "The additional amount will be charged to your wallet at this municipality's rate.",
  'citizen.parking.extend.noOptions': 'This municipality does not offer extensions for this stay.',
  'citizen.parking.extend.submit': 'Extend',

  'citizen.parking.finish.confirmTitle': 'Finish this parking session?',
  'citizen.parking.finish.immediate': 'The session ends immediately and bay {{space}} becomes free.',
  'citizen.parking.finish.confirmDescription.credited':
    'You have {{minutes}} left. They will be saved as time credit in this municipality and expire on {{date}}. They are used first in your next session here; they are not money and cannot be used in another municipality.',
  'citizen.parking.finish.confirmDescription.lost':
    "You have {{minutes}} left. This municipality doesn't credit remaining time: those minutes are lost and won't be refunded to your wallet.",
  'citizen.parking.finish.confirmSubmit': 'Yes, finish',

  'citizen.parking.error.title': "We couldn't start your parking session",
  'citizen.parking.error.SESSION_ALREADY_ACTIVE_FOR_VEHICLE': 'This vehicle already has an active parking session.',
  'citizen.parking.error.SESSION_ALREADY_ACTIVE_FOR_PLATE':
    'This plate already has a running stay in this municipality.',
  'citizen.parking.error.SPACE_OCCUPIED': 'This space is already occupied.',
  'citizen.parking.error.INVALID_INCREMENT': 'The selected duration is not valid.',
  'citizen.parking.error.INSUFFICIENT_BALANCE': 'Insufficient balance. Top up your wallet to continue.',
  'citizen.parking.error.EXTENSION_DISABLED': "This municipality doesn't allow extending time.",
  'citizen.parking.error.EXTENSION_EXCEEDS_MAX': 'The extension exceeds the maximum allowed for this session.',
  'citizen.parking.error.EARLY_FINISH_DISABLED': "This municipality doesn't allow finishing early.",
  'citizen.parking.error.VEHICLE_NOT_FOUND': 'The selected vehicle was not found.',
  'citizen.parking.error.SESSION_NOT_FOUND': 'The parking session was not found.',
  'citizen.parking.error.PARKING_SESSION_NOT_ACTIVE': 'This session is no longer active.',
  'citizen.parking.error.PARKING_SPACE_NOT_FOUND': 'There is no bay with that code in the chosen zone.',
  'citizen.parking.error.PARKING_SPACE_OUT_OF_SERVICE': 'That bay is out of service.',
  'citizen.parking.error.PARKING_SPACE_CODE_INVALID': "That code is not in the format this municipality uses.",
  'citizen.parking.error.OUTSIDE_CHARGING_HOURS': 'Parking is not charged at this hour in this municipality.',

  'citizen.vehicles.title': 'My vehicles',
  'citizen.vehicles.addCta': 'Add',
  'citizen.vehicles.primaryBadge': 'Primary',
  'citizen.vehicles.editCta': 'Edit',
  'citizen.vehicles.menuCta': 'More options',
  'citizen.vehicles.menuForPlate': 'Options for {{plate}}',
  'citizen.vehicles.imageIsGeneric':
    'The picture and category are estimated from make and model; you can correct them by editing the vehicle.',
  'citizen.vehicles.form.typeAutoChosen': 'Car (the app picks)',
  'citizen.vehicles.imageAlt': 'Generic illustration of a vehicle',
  'citizen.vehicles.empty.title': "You don't have any vehicles yet!",
  'citizen.vehicles.empty.description': 'Add a vehicle to be able to park in this municipality.',
  'citizen.vehicles.activeSessionBadge': 'In use',
  'citizen.vehicles.actions.setPrimary': 'Set as primary',
  'citizen.vehicles.actions.delete': 'Delete',

  'citizen.vehicles.form.title.add': 'Add vehicle',
  'citizen.vehicles.form.title.edit': 'Edit vehicle',
  'citizen.vehicles.form.plateLabel': 'Plate',
  'citizen.vehicles.form.plateHint': 'Saved in uppercase, with no spaces or dashes.',
  'citizen.vehicles.form.nameLabel': 'Vehicle name',
  'citizen.vehicles.form.typeLabel': 'Vehicle type',
  'citizen.vehicles.form.typeHint': 'The body style only changes the picture you see in this app; charging is by car or motorcycle.',
  'citizen.vehicles.body.sedan': 'Sedan',
  'citizen.vehicles.body.hatchback': 'Hatchback',
  'citizen.vehicles.body.suv': 'SUV',
  'citizen.vehicles.body.pickup': 'Pick-up',
  'citizen.vehicles.body.minivan': 'Minivan',
  'citizen.vehicles.body.van': 'Van',
  'citizen.vehicles.form.colorLabel': 'Colour',
  'citizen.vehicles.form.colorUnset': 'Not specified',
  'citizen.vehicles.form.brandLabel': 'Brand',
  'citizen.vehicles.form.modelLabel': 'Model',
  'citizen.vehicles.form.yearLabel': 'Year',
  'citizen.vehicles.form.isOwnerLabel': "I'm the owner",
  'citizen.vehicles.form.submit.add': 'Add vehicle',
  'citizen.vehicles.form.submit.edit': 'Save changes',
  'citizen.vehicles.form.error.PLATE_ALREADY_REGISTERED': 'You already have a vehicle with this plate.',

  'citizen.vehicles.delete.confirmTitle': 'Delete vehicle',
  'citizen.vehicles.delete.confirmDescription': 'Are you sure you want to delete vehicle {{plate}}? This action cannot be undone.',
  'citizen.vehicles.delete.confirmSubmit': 'Yes, delete',
  'citizen.vehicles.delete.error.VEHICLE_HAS_ACTIVE_SESSION':
    "You can't delete this vehicle: it has an active parking session. Finish it first.",

  'citizen.fines.title': 'Fines',
  'citizen.fines.tab.pending': 'Pending',
  'citizen.fines.pendingWarning.one': 'You have {{count}} unpaid fine. Pay it to avoid surcharges.',
  'citizen.fines.pendingWarning.other': 'You have {{count}} unpaid fines. Pay them to avoid surcharges.',
  'citizen.fines.help.title': 'Have a question?',
  'citizen.fines.help.meta': 'See how fines work',
  'citizen.fines.tab.history': 'History',
  'citizen.fines.empty.title': 'All clear!',
  'citizen.fines.empty.description': "You don't have any pending fines in this municipality.",
  'citizen.fines.dueLabel': 'Due on',

  'citizen.wallet.title': 'Wallet',
  'citizen.wallet.balanceLabel': 'Available balance',
  'citizen.wallet.topUpCta': 'Top up balance',
  'citizen.wallet.paymentMethods.title': 'Payment methods',
  'citizen.wallet.paymentMethods.viewAllCta': 'View all',
  'citizen.wallet.paymentMethods.addCta': 'Add card',
  'citizen.wallet.paymentMethods.expiresLabel': 'Expires {{date}}',
  'citizen.wallet.paymentMethods.primaryBadge': 'Primary',
  'citizen.wallet.movements.viewAllCta': 'View all',
  'citizen.wallet.timeCredits.title': 'Time credit',
  'citizen.wallet.timeCredits.value.one': '{{count}} minute',
  'citizen.wallet.timeCredits.value.other': '{{count}} minutes',
  'citizen.wallet.timeCredits.expiresLabel': 'Expires on {{date}}',
  'citizen.wallet.timeCredits.noExpiry': "Doesn't expire",
  'citizen.wallet.timeCredits.empty': "You don't have time credit in this municipality.",

  'citizen.movements.balanceAfter': 'Balance after: {{amount}}',
  'citizen.movements.testMoney': 'TEST MODE · simulated balance',
  'citizen.wallet.testMoney.badge': 'TEST MODE',
  'citizen.wallet.testMoney.notice': 'Some of this balance was created by the payment simulator: nobody paid for it and it only works in development.',
  'citizen.topup.title': 'Top up balance',
  'citizen.topup.amountLegend': 'How much do you want to add?',
  'citizen.topup.continue': 'Continue to payment',
  'citizen.topup.cancel': 'Cancel',
  'citizen.topup.close': 'Close',
  'citizen.topup.simulatedNotice': 'Simulated payment: nothing is charged and no card details are asked for.',
  'citizen.topup.unavailable': 'This municipality does not accept card payments yet.',
  'citizen.topup.starting': 'Opening the payment…',
  'citizen.topup.error': 'The payment could not be opened. Try again.',
  'citizen.topup.range': 'Between {{min}} and {{max}}.',
  'citizen.topup.result.credited': 'Balance credited',
  'citizen.topup.result.creditedDetail': '{{amount}} was added to your balance.',
  'citizen.topup.result.pending': 'The payment is still in progress',
  'citizen.topup.result.pendingDetail': 'Nothing has been credited yet. Check back later.',
  'citizen.topup.result.failed': 'The payment did not go through',
  'citizen.topup.result.cancelled': 'You cancelled the payment',
  'citizen.topup.result.noCharge': 'Nothing was charged and your balance did not change.',
  'citizen.topup.result.card': 'Card ending in {{last4}}.',
  'citizen.simulatedPay.title': 'Simulated payment page',
  'citizen.simulatedPay.notice': 'This is NOT a payment provider. It is a test screen: pick the outcome you want it to return. No card details are asked for or stored.',
  'citizen.simulatedPay.amount': 'Amount: {{amount}}',
  'citizen.simulatedPay.scenarioLegend': 'Which outcome do you want to test?',
  'citizen.simulatedPay.pay': 'Pay',
  'citizen.simulatedPay.cancel': 'Cancel and go back',
  'citizen.simulatedPay.challengeTitle': 'Issuing bank authentication',
  'citizen.simulatedPay.challengeDetail': 'The bank asks for an extra confirmation before approving.',
  'citizen.simulatedPay.challengeApprove': 'Complete the authentication',
  'citizen.simulatedPay.challengeReject': 'Do not complete it',
  'citizen.simulatedPay.scenario.VISA_APPROVED': 'Visa · approved',
  'citizen.simulatedPay.scenario.MASTERCARD_APPROVED': 'Mastercard · approved',
  'citizen.simulatedPay.scenario.DECLINED': 'Declined by the issuer',
  'citizen.simulatedPay.scenario.INSUFFICIENT_FUNDS': 'Insufficient funds',
  'citizen.simulatedPay.scenario.THREE_DS_CHALLENGE': 'Asks for bank authentication',
  'citizen.simulatedPay.scenario.EXPIRED_CARD': 'Expired card',
  'payment.failure.DECLINED': 'The issuer declined the transaction.',
  'payment.failure.INSUFFICIENT_FUNDS': 'Insufficient funds on the card.',
  'payment.failure.EXPIRED_CARD': 'The card has expired.',
  'payment.failure.AUTHENTICATION_FAILED': 'The issuing bank authentication was not completed.',
  'citizen.movements.title': 'Movements',
  'citizen.movements.filter.all': 'All',
  'citizen.movements.filter.parking': 'Parking',
  'citizen.movements.filter.topups': 'Top-ups',
  'citizen.movements.filter.fines': 'Fines',
  'citizen.movements.empty.title': 'No movements',
  'citizen.movements.empty.description': "There aren't any movements yet with this filter in this municipality.",
  'citizen.movements.item.parking': 'Parking payment',
  'citizen.movements.item.parkingExtension': 'Parking time extension',
  'citizen.movements.item.topup': 'Balance top-up',
  'citizen.movements.item.fine': 'Fine',
  'citizen.movements.item.adjustment': 'Adjustment',

  'citizen.profile.title': 'My account',
  'citizen.profile.personalData.label': 'Personal details',
  'citizen.profile.personalData.description':
    'Your name, identity document, phone and address. They are saved together.',
  'citizen.profile.phoneLabel': 'Phone',
  'citizen.profile.identification.label': 'Identification',
  'citizen.profile.identification.value': 'National ID or DIMEX',
  'citizen.profile.address.label': 'Address',
  'citizen.profile.security.label': 'Security',
  'citizen.profile.security.value': 'Change password',
  'citizen.profile.language.label': 'Language',
  'citizen.profile.editCta': 'Edit',
  'citizen.profile.address.empty': 'No address on file.',
  'citizen.profile.phone.empty': 'No phone on file.',
  'citizen.profile.identification.empty': 'No document on file.',
  'citizen.profile.loadError': 'Your account could not be loaded.',

  'inspector.home.offline': 'Offline — data saved locally',
  'inspector.home.online': 'Online',
  'inspector.home.quick.lookup': 'Check a plate',
  'inspector.home.quick.cite': 'Write a citation',
  'inspector.home.quick.evidence': 'Evidence',
  'inspector.home.quick.queue': 'Pending',
  'inspector.home.quick.label': 'Shift actions',
  'inspector.home.quick.title': 'Quick actions',
  'inspector.home.title': 'Check a vehicle',
  'inspector.home.subtitle': 'Check the parking status and any permits.',
  'inspector.home.shift.checks': 'Lookups today',
  'inspector.home.shift.citations': 'Citations issued',
  'inspector.home.shift.pending': 'Waiting to send',
  'inspector.home.location.title': 'Where this lookup is',
  'inspector.home.clock.label': "This device's date and time",

  'inspector.home.greeting': 'Hello, Officer',
  'inspector.home.greeting.named': 'Hello, {{name}}',
  'inspector.home.where': 'Enforcement · {{tenant}}',
  'inspector.home.where.noTenant': 'Enforcement',
  'inspector.home.quick.lookup.hint': 'Check status and permits',
  'inspector.home.quick.cite.hint': 'Infraction or notice',
  'inspector.home.quick.queue.hint': 'To send',
  'inspector.home.quick.evidence.hint': 'Photos and attachments',
  'inspector.home.fast.go': 'Search',
  'inspector.home.last.title': 'Last lookup',
  'inspector.home.last.empty': 'Results will appear here once you check a plate.',
  'inspector.home.last.again': 'Check {{plate}} again',
  'inspector.home.last.checkedAt': 'Checked at {{time}}',
  'home.inspector.title': 'Patrol',
  'inspector.home.patrol.title': "Today's round",
  'home.inspector.patrolStub.title': 'No round yet',
  'home.inspector.patrolStub.description':
    'Once the municipality enables rounds, the assigned zones and the day\'s citations appear here.',

  // ---- Platform back-office (CONTRACT.md §4 `/api/v1/platform/**`) -----------------------------

  'platform.banner.default': 'You are operating at platform scope — access to every municipality',
  'platform.nav.tenants': 'Municipalities',
  'platform.nav.users': 'Global registry',
  'platform.nav.audit': 'Global audit log',
  'platform.nav.reports': 'Reports',
  'platform.nav.catalogs': 'Catalogs',
  'platform.nav.system': 'System',

  'platform.tenants.title': 'Municipalities',
  'platform.tenants.searchPlaceholder': 'Search by name or slug',
  'platform.tenants.filter.status': 'Status',
  'platform.tenants.filter.allStatuses': 'All statuses',
  'platform.tenants.filter.country': 'Country',
  'platform.tenants.filter.allCountries': 'All countries',
  'platform.tenants.column.name': 'Municipality',
  'platform.tenants.column.slug': 'Slug',
  'platform.tenants.column.country': 'Country',
  'platform.tenants.column.currency': 'Currency',
  'platform.tenants.column.status': 'Status',
  'platform.tenants.empty': 'No municipalities matched those criteria.',
  'platform.tenants.createCta': 'New municipality',
  'platform.tenants.status.ACTIVE': 'Active',
  'platform.tenants.status.SUSPENDED': 'Suspended',
  'platform.tenants.status.CLOSED': 'Closed',

  'platform.tenants.create.title': 'New municipality',
  'platform.tenants.create.slugLabel': 'Slug',
  'platform.tenants.create.legalNameLabel': 'Legal name',
  'platform.tenants.create.displayNameLabel': 'Display name',
  'platform.tenants.create.countryLabel': 'Country',
  'platform.tenants.create.currencyLabel': 'Currency',
  'platform.tenants.create.localeLabel': 'Locale',
  'platform.tenants.create.timeZoneLabel': 'Time zone',
  'platform.tenants.create.selfRegistrationLabel': 'Self-registration policy',
  'platform.tenants.create.selfRegistration.OPEN': 'Open',
  'platform.tenants.create.selfRegistration.APPROVAL_REQUIRED': 'Approval required',
  'platform.tenants.create.selfRegistration.INVITE_ONLY': 'Invite only',
  'platform.tenants.create.submit': 'Create municipality',
  'platform.tenants.create.success': 'Municipality created successfully.',

  'platform.tenants.detail.title': 'Municipality detail',
  'platform.tenants.detail.statusSectionTitle': 'Status',
  'platform.tenants.detail.changeStatusReasonLabel': 'Reason for change',
  'platform.tenants.detail.changeStatusSubmit': 'Apply status change',
  'platform.tenants.detail.settingsTitle': 'Settings',
  'platform.tenants.detail.settingsSave': 'Save settings',
  'platform.tenants.detail.settingsSaved': 'Settings saved.',
  'platform.tenants.detail.createAdminTitle': 'Create the municipality administrator',
  'platform.tenants.detail.createAdminEmailLabel': 'Email address',
  'platform.tenants.detail.createAdminGivenNameLabel': 'Given name',
  'platform.tenants.detail.createAdminFamilyNameLabel': 'Family name',
  'platform.tenants.detail.createAdminSubmit': 'Create administrator',
  'platform.tenants.detail.createAdminSuccess': 'Administrator created and invited successfully.',

  'platform.users.title': 'Global registry',
  'platform.users.searchPlaceholder': 'Search by name, email or document',
  'platform.users.filter.country': 'Country',
  'platform.users.filter.allCountries': 'All countries',
  'platform.users.filter.status': 'Status',
  'platform.users.filter.allStatuses': 'All statuses',
  'platform.users.filter.tenant': 'Municipality',
  'platform.users.filter.allTenants': 'All municipalities',
  'platform.users.column.name': 'Name',
  'platform.users.column.email': 'Email',
  'platform.users.column.status': 'Status',
  'platform.users.column.memberships': 'Memberships',
  'platform.users.empty': 'No people matched those criteria.',
  'platform.users.detail.title': 'User detail',
  'platform.users.detail.membershipsTitle': 'Memberships',
  'platform.users.detail.noMemberships': "This person doesn't have memberships in any municipality.",
  'platform.users.detail.createMembershipCta': 'Grant membership',
  'platform.users.detail.createMembership.title': 'Grant membership',
  'platform.users.detail.createMembership.tenantLabel': 'Municipality',
  'platform.users.detail.createMembership.portalLabel': 'Portal',
  'platform.users.detail.createMembership.roleLabel': 'Role',
  'platform.users.detail.createMembership.submit': 'Grant',
  'platform.users.detail.actionSuccess': 'The action was applied successfully.',

  'platform.audit.filter.tenant': 'Municipality',
  'platform.audit.filter.allTenants': 'All municipalities',

  'platform.catalogs.title': 'Catalogs',
  'platform.catalogs.tab.countries': 'Countries',
  'platform.catalogs.tab.adminLevels': 'Administrative levels',
  'platform.catalogs.tab.divisions': 'Administrative divisions',
  'platform.catalogs.tab.documentTypes': 'Document types',
  'platform.catalogs.countries.column.code': 'Code',
  'platform.catalogs.countries.column.name': 'Name',
  'platform.catalogs.countries.column.dialCode': 'Dial code',
  'platform.catalogs.countries.column.currency': 'Currency',
  'platform.catalogs.countries.column.locale': 'Locale',
  'platform.catalogs.countries.column.timeZone': 'Time zone',
  'platform.catalogs.selectCountry': 'Country',
  'platform.catalogs.adminLevels.column.level': 'Level',
  'platform.catalogs.adminLevels.column.labelKey': 'Label key',
  'platform.catalogs.adminLevels.column.required': 'Required',
  'platform.catalogs.divisions.column.code': 'Code',
  'platform.catalogs.divisions.column.name': 'Name',
  'platform.catalogs.divisions.column.level': 'Level',
  'platform.catalogs.documentTypes.column.type': 'Type',
  'platform.catalogs.documentTypes.column.pattern': 'Pattern',
  'platform.catalogs.documentTypes.column.example': 'Example',
  'platform.catalogs.documentTypes.column.default': 'Default',

  'platform.system.title': 'System',
  'platform.system.health.title': 'Service health',
  'platform.system.featureFlags.title': 'Feature flags',
  'platform.system.jobs.title': 'Scheduled jobs',
  'platform.system.extensionNotice': "This section is prepared: what else it controls is defined later.",

  // ---- Weekdays (charging schedule, CONTRACT.md v0.3) -----------------------------------------
  'weekday.monday': 'Monday',
  'weekday.tuesday': 'Tuesday',
  'weekday.wednesday': 'Wednesday',
  'weekday.thursday': 'Thursday',
  'weekday.friday': 'Friday',
  'weekday.saturday': 'Saturday',
  'weekday.sunday': 'Sunday',

  'common.languageSwitcher.unavailable': 'not available in this version',
  'nav.settings': 'Settings',

  // ---- My account (CONTRACT.md v0.3 "Perfil editable") -----------------------------------------
  'account.profile.saved': 'Your details were saved.',
  'account.language.meta': 'Languages enabled by your municipality.',
  'account.password.label': 'Password',
  'account.password.meta': 'Change it whenever you want.',
  'account.password.currentLabel': 'Current password',
  'account.password.newLabel': 'New password',
  'account.password.confirmLabel': 'Confirm the new password',
  'account.password.submit': 'Change password',
  'account.password.changeCta': 'Change password',
  'account.password.sessionsWarning':
    'Changing it closes every session, this one included: you will have to sign in again with the new password.',
  'account.password.done': 'Done: your password changed and every session was closed.',
  'account.password.signInAgain': 'Sign in again',
  'account.password.error.currentInvalid': 'That is not your current password.',
  'account.email.title': 'Email address',
  'account.email.meta': 'The address you sign in with.',
  'account.email.currentLabel': 'Current email',
  'account.email.newLabel': 'New email',
  'account.email.submit': 'Send verification',
  'account.email.changeCta': 'Change email',
  'account.email.verificationNotice':
    'We send a link to the new address. Your current email keeps working until you verify the new one.',
  'account.email.pending': 'Check {{email}} and open the link to finish the change.',
  'account.email.error.sameAsCurrent': 'That is already your current email.',
  'account.email.error.alreadyInUse': 'That email is already used by another account.',

  // ---- Municipality settings (CONTRACT.md v0.3) ------------------------------------------------
  'admin.settings.saved': 'Changes saved.',
  'admin.settings.locales.title': 'Languages',
  'admin.settings.locales.description':
    "Languages this municipality's portals offer, and which one is used when someone has not chosen.",
  'admin.settings.locales.defaultBadge': 'Default',
  'admin.settings.locales.makeDefault': 'Make default',
  'admin.settings.locales.platformDefault': 'If this municipality resolves to nothing, the platform uses {{locale}}.',
  'admin.settings.locales.error.noneEnabled': 'At least one language has to stay enabled.',
  'admin.settings.locales.error.defaultRequired': 'Pick exactly one default among the enabled languages.',
  'admin.settings.spaceFormat.title': 'Bay code format',
  'admin.settings.spaceFormat.description':
    'How the code painted on each bay is written. The server validates against this format when bays are created and when a session starts.',
  'admin.settings.spaceFormat.prefixLabel': 'Prefix',
  'admin.settings.spaceFormat.prefixHint': 'What goes before the number, e.g. "SJ-". Leave empty for digits only.',
  'admin.settings.spaceFormat.digitsLabel': 'Number of characters',
  'admin.settings.spaceFormat.digitsHint': 'How many characters follow the prefix, zero-padded.',
  'admin.settings.spaceFormat.allowLettersLabel': 'Allow letters',
  'admin.settings.spaceFormat.allowLettersHint': 'When ticked, the code accepts letters as well as digits.',
  'admin.settings.spaceFormat.previewLabel': 'This is how it looks',
  'admin.settings.spaceFormat.stored': 'In force now: {{example}} ({{pattern}})',
  'admin.settings.spaceFormat.error.digitsRange': 'The number of characters has to be between {{min}} and {{max}}.',
  'admin.settings.schedule.title': 'Charging hours',
  'admin.settings.schedule.description':
    "Only the minutes inside a band are charged. Everything is evaluated in the municipality's time zone ({{timeZone}}).",
  'admin.settings.schedule.allDayLabel': 'Charge around the clock',
  'admin.settings.schedule.allDayHint': 'When ticked, the bands and exceptions below are ignored.',
  'admin.settings.schedule.freeDaysSummary': 'No charge on: {{days}}.',
  'admin.settings.schedule.noFreeDays': 'Every day is charged. No day is free.',
  'admin.settings.schedule.dayFree': 'No charge',
  'admin.settings.schedule.addBand': 'Add band',
  'admin.settings.schedule.removeBand': 'Remove',
  'admin.settings.schedule.fromLabel': 'From',
  'admin.settings.schedule.toLabel': 'To',
  'admin.settings.schedule.error.invalidBand': 'Check the hours for {{day}}: the end has to be after the start.',
  'admin.settings.schedule.confirm.title': 'Save the charging schedule?',
  'admin.settings.schedule.confirm.body':
    'It changes from now on when charging applies across the municipality. Stays already ' +
    'running keep what they were charged; the change applies to stays starting after it.',
  'admin.settings.schedule.confirm.days': 'Days with charging',
  'admin.settings.schedule.confirm.allDay': 'Charges 24 hours',
  'admin.settings.schedule.confirm.exceptions': 'Exceptions',
  'admin.settings.schedule.chargingNow': 'Parking is being charged right now.',
  'admin.settings.schedule.notChargingNow': 'Nothing is being charged right now. Charging resumes {{resumesAt}}.',
  'admin.settings.schedule.exceptions.title': 'Date exceptions',
  'admin.settings.schedule.exceptions.description': 'Public holidays and special days: they override the weekly schedule.',
  'admin.settings.schedule.exceptions.empty': 'No exceptions yet.',
  'admin.settings.schedule.exceptions.add': 'Add exception',
  'admin.settings.schedule.exceptions.remove': 'Remove',
  'admin.settings.schedule.exceptions.repeatLabel': 'Repeats',
  'admin.settings.schedule.exceptions.repeat.once': 'This date only',
  'admin.settings.schedule.exceptions.repeat.annual': 'Every year, same day',
  'admin.settings.schedule.exceptions.repeat.easter': 'With Easter',
  'admin.settings.schedule.exceptions.monthLabel': 'Month',
  'admin.settings.schedule.exceptions.dayLabel': 'Day',
  'admin.settings.schedule.exceptions.mondayLabel': 'Moves to the Monday',
  'admin.settings.schedule.exceptions.easterLabel': 'Which day',
  'admin.settings.schedule.exceptions.easter.maundyThursday': 'Maundy Thursday',
  'admin.settings.schedule.exceptions.easter.goodFriday': 'Good Friday',
  'admin.settings.schedule.exceptions.easter.sunday': 'Easter Sunday',
  'admin.settings.schedule.exceptions.easter.monday': 'Easter Monday',
  'admin.settings.schedule.exceptions.legalDate': 'Statutory date: {{date}}',
  'admin.settings.schedule.exceptions.effectiveDate': 'No charging on {{date}}',
  'admin.settings.schedule.exceptions.effectivePast': 'No charging on {{date}} · already past',
  'admin.settings.schedule.exceptions.effectiveUnknown': 'No date yet: the rule is incomplete.',
  'admin.settings.schedule.holidays.title': "The country's holidays",
  'admin.settings.schedule.holidays.description':
    'So nobody retypes them every December. Adding one makes it an exception of yours, which you can edit or remove whenever you like.',
  'admin.settings.schedule.holidays.notice':
    'A starting point, not legal advice: holiday law changes and this municipality is answerable for its own charging calendar. Check each one before saving.',
  'admin.settings.schedule.holidays.empty': 'The platform does not have this country\'s calendar yet.',
  'admin.settings.schedule.holidays.add': 'Add',
  'admin.settings.schedule.holidays.added': 'Already there',
  'admin.settings.schedule.exceptions.dateLabel': 'Date',
  'admin.settings.schedule.exceptions.labelLabel': 'Holiday name',
  'admin.settings.schedule.exceptions.chargesLabel': 'Charged on that day',
  'admin.settings.schedule.exceptions.allDayLabel': 'Around the clock',

  // ---- Citizen: schedule and chargeable minutes (CONTRACT.md v0.3) ------------------------------
  'citizen.parking.schedule.notCharging': 'This municipality is not charging right now.',
  'citizen.parking.schedule.notChargingWithResume': 'No charge right now; charging resumes {{resumesAt}}.',
  'citizen.parking.map.open': 'See the zone on the map',
  'citizen.parking.map.title': "This municipality's charging zones",
  'citizen.parking.map.expand': 'Expand',
  'citizen.parking.map.collapse': 'Close',
  'citizen.parking.map.loading': 'Loading the map…',
  'citizen.parking.map.empty': 'This municipality has no verified zones on the map yet. Parking still works: the zone and bay you typed are what count.',
  'citizen.parking.map.error': 'The map could not be loaded. You can carry on without it.',
  'citizen.parking.map.noProvider': 'This installation has no map provider configured.',
  'citizen.parking.map.notOfficial': 'These are not official boundaries.',
  'citizen.parking.map.notOfficialDetail': 'Dashed ones are demonstration data.',
  'citizen.parking.map.tapHint': 'Tap a zone on the map, or pick one from the list.',
  'citizen.parking.step1.zonePlaceholder': 'Select the zone',
  'citizen.parking.map.emptyShort': 'This municipality has no zones drawn on the map yet.',

  'citizen.parking.step1.spaceCodeLabel': 'Parking number',
  'citizen.parking.step1.spaceCodeHint': 'The number painted on the bay you are in.',
  'citizen.parking.step1.noZones': 'This municipality has not published its parking zones yet.',
  'citizen.parking.step4.chargeableMinutesLabel': 'Chargeable minutes',
  'citizen.parking.step4.chargeableMinutesHint': 'The rest falls outside the charging hours.',
  'citizen.parking.step4.courtesyHint': 'A courtesy stay: nothing is charged.',

  // ---- Citizen: bay-code guide (the zone's real range + the municipality's format) --------------
  'citizen.parking.step1.spaceCodeRange': 'Bays {{first}}–{{last}} in this zone ({{count}} in total).',
  'citizen.parking.step1.spaceCodeRangeSingle': 'This zone has a single bay: {{code}}.',
  'citizen.parking.step1.spaceCodeFormat': 'Format: {{example}}.',
  'citizen.parking.step1.spaceCodeError.format':
    "That code does not match this municipality's format. For example: {{example}}.",
  'citizen.parking.step1.spaceCodeError.range': 'This zone only has bays from {{first}} to {{last}}.',

  // ---- Citizen: "Other" duration, inside the municipality's own limits --------------------------
  'citizen.parking.step3.maxNotice': 'This municipality allows up to {{max}} per stay.',
  'citizen.parking.step3.maxNoticeZone': 'This zone allows up to {{max}} per stay.',
  'citizen.parking.step3.courtesyNotice':
    'The first {{minutes}} are free in this zone, once a day per plate.',

  // ---- Catalogs (server-provided keys: countries, documents, divisions) -------------------------
  'country.CR': 'Costa Rica',
  'country.ES': 'Spain',
  'country.MX': 'Mexico',
  'country.PA': 'Panama',
  'country.US': 'United States',

  'document.CR.NATIONAL_ID': 'National ID (cédula)',
  'document.CR.FOREIGN_RESIDENT_ID': 'Resident ID (DIMEX)',
  'document.CR.PASSPORT': 'Passport',
  'document.CR.TAX_ID': 'Tax ID (cédula jurídica)',
  'document.CR.OTHER': 'Other document',
  'document.ES.NATIONAL_ID': 'National ID (DNI)',
  'document.ES.FOREIGN_RESIDENT_ID': 'Resident ID (NIE)',
  'document.ES.PASSPORT': 'Passport',
  'document.MX.NATIONAL_ID': 'National ID (CURP)',
  'document.MX.PASSPORT': 'Passport',
  'document.MX.TAX_ID': 'Tax ID (RFC)',
  'document.PA.NATIONAL_ID': 'National ID (cédula)',
  'document.PA.PASSPORT': 'Passport',
  'document.US.NATIONAL_ID': "Driver's licence",
  'document.US.PASSPORT': 'Passport',
  'document.US.TAX_ID': 'Tax ID (SSN)',

  // Vehicle types and colours: the server sends `{value, labelKey}` and the client only translates.
  'vehicle.type.car': 'Car',
  'vehicle.type.motorcycle': 'Motorcycle',
  'vehicle.type.truck': 'Truck',
  'vehicle.type.pickup': 'Pickup',
  'vehicle.type.van': 'Van',
  'vehicle.type.bus': 'Bus',
  'vehicle.type.bicycle': 'Bicycle',
  'vehicle.type.scooter': 'Scooter',
  'vehicle.type.other': 'Other',

  'vehicle.color.white': 'White',
  'vehicle.color.black': 'Black',
  'vehicle.color.gray': 'Grey',
  'vehicle.color.grey': 'Grey',
  'vehicle.color.silver': 'Silver',
  'vehicle.color.red': 'Red',
  'vehicle.color.blue': 'Blue',
  'vehicle.color.green': 'Green',
  'vehicle.color.yellow': 'Yellow',
  'vehicle.color.orange': 'Orange',
  'vehicle.color.brown': 'Brown',
  'vehicle.color.beige': 'Beige',
  'vehicle.color.gold': 'Gold',
  'vehicle.color.purple': 'Purple',
  'vehicle.color.other': 'Other',

  // ---- Enforcement (CONTRACT.md v0.7) ---------------------------------------------------------

  'citation.status.draft': 'Draft',
  'citation.status.issued': 'Issued',
  'citation.status.paid': 'Paid',
  'citation.status.appealed': 'Under appeal',
  'citation.status.upheld': 'Appeal rejected',
  'citation.status.dismissed': 'Appeal upheld',
  'citation.status.cancelled': 'Annulled',
  'citation.status.expired': 'Expired',

  'citation.action.drafted': 'Captured',
  'citation.action.issued': 'Issued',
  'citation.action.evidence_attached': 'Evidence attached',
  'citation.action.paid': 'Paid',
  'citation.action.appealed': 'Appeal filed',
  'citation.action.appeal_upheld': 'Appeal rejected',
  'citation.action.appeal_dismissed': 'Appeal upheld',
  'citation.action.cancelled': 'Annulled',
  'citation.action.expired': 'Expired',

  'citation.evidence.photo': 'Photograph',
  'citation.evidence.note': 'Note',
  'citation.evidence.title': 'Evidence',
  'citation.evidence.empty': 'No evidence is attached to this citation.',
  'citation.evidence.withheld':
    'This citation has evidence, but we cannot show it to you: the vehicle is not registered to you.',
  'citation.evidence.digest': 'SHA-256 digest',
  'citation.evidence.capturedAt': 'Captured',
  'citation.evidence.size': 'Size',
  'citation.evidence.open': 'View photograph',
  'citation.evidence.loadError': 'The photograph could not be loaded.',
  'citation.history.title': 'History',
  'citation.history.reason': 'Reason',
  'citation.history.portal.citizen': 'Citizen portal',
  'citation.history.portal.admin': 'Administration',
  'citation.history.portal.inspector': 'Enforcement',
  'citation.history.portal.platform': 'Platform',

  'citation.field.number': 'Number',
  'citation.source.luparx': 'This municipality',
  'citation.source.external': 'Another system',
  'citation.source.external.named': 'Another system · {{system}}',
  'citation.source.mirrorNotice':
    'This citation was raised in another of the municipality\u2019s systems. It is read here; it is settled there.',
  'citation.source.mirrorNotice.named':
    'This citation was raised in {{system}}, another of the municipality\u2019s systems. It is read here; it is settled there.',
  'citation.field.externalStatus': 'Status in the other system',
  'citation.field.lastSeenAt': 'Last confirmed by the other system',
  'admin.enforcement.citation.notManagedHere':
    'This citation was raised in another system. It is annulled there, not here.',
  'citizen.fines.managedElsewhere':
    'The municipality manages this fine in another system. You can read it here, but pay it or file your defence where the municipality tells you.',
  'citizen.fines.managedElsewhere.named':
    'The municipality manages this fine in {{system}}. You can read it here, but pay it or file your defence where the municipality tells you.',
  'citizen.fines.payElsewhere': 'This fine is not paid in LupaRX: the municipality collects it in its other system.',
  'citation.field.plate': 'Plate',
  'citation.field.infraction': 'Infraction',
  'citation.field.zone': 'Zone',
  'citation.field.bay': 'Bay',
  'citation.field.address': 'Address',
  'citation.field.coordinates': 'Coordinates',
  'citation.field.accuracy': 'Accuracy',
  'citation.field.occurredAt': 'Declared time',
  'citation.field.issuedAt': 'Issued at',
  'citation.field.clockSkew': 'Device clock offset',
  'citation.field.fine': 'Amount',
  'citation.field.amountPayable': 'Amount payable today',
  'citation.field.discountUntil': 'Discount until',
  'citation.field.dueAt': 'Due',
  'citation.field.notes': 'Notes',
  'citation.field.inspector': 'Officer',
  'citation.field.statusReason': 'Reason for the status',
  'citation.field.noNumber': 'No number (draft)',
  'citation.field.noCoordinates': 'No coordinates',
  'citation.field.noCoordinates.hint':
    'The citation was issued without GPS. It is recorded that no coordinates were taken.',
  'citation.clockSkew.seconds': '{{seconds}} s',

  'plate.verdict.unknown': 'Unresolved',
  'plate.verdict.exempt': 'Exempt',
  'plate.verdict.expired': 'Payment expired',
  'plate.verdict.covered': 'Paid for this bay',
  'plate.verdict.bay_mismatch': 'Paid for a different bay',
  'plate.verdict.not_covered': 'No running session',
  'plate.verdict.ambiguous': 'The bay is missing',

  // ---- Enforcement app -------------------------------------------------------------------------

  'inspector.nav.lookup': 'Lookup',
  'inspector.nav.cite': 'Citation',
  'inspector.nav.citations': 'My citations',
  'inspector.nav.queue': 'Pending',
  'inspector.nav.more': 'More',

  'inspector.lookup.title': 'Plate lookup',
  'inspector.lookup.help': 'Check permits, infractions and the vehicle details on record.',
  'inspector.lookup.offline': 'No signal: a lookup needs a connection. Citations can still be written and will send themselves when it returns.',
  'inspector.lookup.checking': 'Checking the plate…',
  'inspector.lookup.plateLabel': 'Vehicle plate',
  'inspector.lookup.platePlaceholder': 'Enter the vehicle plate',
  'inspector.lookup.zoneLabel': 'Zone',
  'inspector.lookup.zonePlaceholder': 'Select a zone',
  'inspector.lookup.bayLabel': 'Bay',
  'inspector.lookup.bayPlaceholder': '0042',
  'inspector.lookup.submit': 'Look up',
  'inspector.lookup.bayHint': 'Without the bay, coverage is never confirmed.',
  'inspector.lookup.bayIncomplete': 'Give the zone and the bay together, or neither of them.',
  'inspector.lookup.checkedAt': 'Checked at {{time}}',
  'inspector.lookup.coveringStay': 'Session running until {{time}}',
  'inspector.lookup.otherStays': 'Running sessions for this plate on other bays',
  'inspector.lookup.stayRow': '{{zone}} · bay {{bay}} · until {{time}}',
  'inspector.lookup.useThisBay': 'Look up with this bay',
  'inspector.lookup.verdict.covered.detail':
    'This plate has a running parking session on bay {{bay}}. No citation is due for non-payment.',
  'inspector.lookup.verdict.bay_mismatch.detail':
    'It paid for bay {{other}}, not for this one ({{bay}}). That is a different infraction from not paying: check the type before issuing.',
  'inspector.lookup.verdict.not_covered.detail':
    'There is no running parking session for this plate anywhere in this municipality.',
  'inspector.lookup.verdict.ambiguous.detail':
    'Sessions are running for this plate, but without the bay there is no way to tell whether they belong to this car. Give the bay.',
  'inspector.lookup.cite': 'Write a citation',
  'inspector.lookup.verdict.exempt.detail':
    'This municipality exempts this plate. No non-payment citation is due, whether or not it paid.',
  'inspector.lookup.verdict.expired.detail':
    'It paid for this very bay and the time ran out. That is not the same as never having paid: check the infraction type before issuing.',
  'inspector.lookup.exemption.reason': 'Reason: {{reason}}',
  'inspector.lookup.exemption.document': 'Backed by: {{ref}}',
  'inspector.lookup.exemption.until': 'In force until {{date}}',
  'inspector.lookup.exemption.noEnd': 'In force with no expiry date',
  'inspector.lookup.stay.startedAt': 'Started: {{datetime}}',
  'inspector.lookup.stay.paid': 'Paid · {{amount}}',
  'inspector.lookup.stay.noCharge': 'No charge',
  'inspector.lookup.stay.noCharge.COURTESY': "No charge · the zone's courtesy",
  'inspector.lookup.stay.noCharge.CREDIT': 'No charge · used saved minutes',
  'inspector.lookup.stay.noCharge.OUTSIDE_HOURS': 'No charge · outside charging hours',
  'inspector.lookup.stay.transaction': 'Txn {{id}}',
  'inspector.lookup.stay.expiresAt': 'Expires: {{datetime}}',
  'inspector.lookup.stay.expiredAt': 'Expired: {{datetime}}',
  'inspector.lookup.stay.zone': 'Zone: {{zone}} · bay {{bay}}',
  'inspector.lookup.stay.remaining': '{{minutes}} min left',
  'inspector.lookup.stay.overdue': 'Ran out {{minutes}} min ago',
  'inspector.lookup.withinGrace':
    'The time has passed, but it is inside this municipality\'s {{minutes}} min tolerance. No non-payment citation is due yet.',
  'inspector.lookup.needBayToCite':
    'Without the bay nothing can be issued: the system does not yet know whether this car paid. Give the bay and look it up again.',
  'inspector.lookup.error.PARKING_SPACE_NOT_FOUND': 'That bay does not exist in the selected zone.',
  'inspector.lookup.error.VALIDATION_FAILED': 'Check the plate and the bay: the zone and the bay travel together.',

  'inspector.zones.title': 'Known zones',
  'inspector.zones.empty': 'No zones are known on this device yet.',
  'inspector.zones.emptyHint':
    'The enforcement portal does not publish the municipality’s zone catalogue yet. Zones are learned from your citations and from plate lookups, and are kept on this device.',

  'inspector.cite.bayBlocked': 'Half the location is missing: give the zone and the bay together, or remove both.',
  'inspector.cite.draftFound': 'An unfinished citation was left, saved {{when}}.',
  'inspector.cite.draftRestore': 'Pick it up',
  'inspector.cite.draftDiscard': 'Discard it',
  'inspector.cite.draftCarries.one': 'It carries plate {{plate}} and one photograph.',
  'inspector.cite.draftCarries.other': 'It carries plate {{plate}} and {{count}} photographs.',
  'inspector.cite.draftNoPlate': 'no plate',
  'inspector.cite.draftOtherVehicle':
    'The draft is for a different plate: it says {{draft}} and this lookup is for {{current}}. Picking it up replaces EVERYTHING on screen — plate, type, location, notes and photographs — nothing is merged.',
  'inspector.cite.draftRestoreReplacing': 'Pick up the draft and replace',
  'inspector.cite.draftKeepLookup': 'Carry on with this lookup',
  'inspector.cite.draftKeepLookupHint':
    'Carrying on with this lookup discards the draft: only one is kept at a time.',
  'inspector.cite.draftPhotosLost.one': 'The device no longer has one of its photographs: it must be taken again.',
  'inspector.cite.draftPhotosLost.other': 'The device no longer has {{count}} of its photographs: they must be taken again.',
  'inspector.queue.error.EVIDENCE_BYTES_MISSING':
    'The photograph is no longer on this device, so this citation cannot be sent as it stands. It will not be retried: take it again, or discard the citation.',
  'inspector.cite.possibleDuplicate':
    'THIS device already has a citation for {{placa}}, captured {{minutos}} min ago. If it is the same act, do not issue it again; if it is a different infraction, carry on.',
  'inspector.cite.possibleDuplicate.bay': 'It was the same bay ({{bahia}}).',
  'inspector.queue.error.EVIDENCE_STORE_FAILED':
    'This device could not store the photograph, so the citation was NOT queued. Free up space — or leave private mode — and capture it again.',
  'inspector.queue.retry.attempted': 'Sending was attempted. Check each citation to see how it went.',
  'inspector.queue.retry.noNetwork': 'Nothing was sent: this device has no network right now.',
  'inspector.queue.retry.inProgress': 'A send is already in progress.',
  'inspector.queue.retry.nothing': 'There was nothing pending to send.',
  'inspector.storage.lowSpace':
    'This device is low on space ({{restante}} of {{total}}). Photographs for the next citations may not be stored: send what is pending now.',
  'inspector.cite.draftNotSaved': 'The device is not saving the draft. If the app closes, what you wrote is lost: issue it before walking away.',
  'inspector.storage.degraded.cola': 'The device is not saving pending citations. They are still on screen, but they will NOT survive the app closing.',
  'inspector.storage.degraded.borrador': 'The device is not saving the draft in progress.',
  'inspector.storage.degraded.fotos': 'The device is not saving photos. The ones already taken are on screen, but will not survive a close.',
  'inspector.cite.title': 'Write a citation',
  'inspector.cite.step.type': 'Infraction type',
  'inspector.cite.step.where': 'Plate and location',
  'inspector.cite.track.title': 'Citation progress',
  'inspector.cite.track.compact': 'Step {{current}} of {{total}}',
  'inspector.cite.track.vehicle': 'Vehicle',
  'inspector.cite.track.infraction': 'Infraction',
  'inspector.cite.track.evidence': 'Evidence',
  'inspector.cite.track.review': 'Review',
  'inspector.cite.typeSearch': 'Search infraction',
  'inspector.cite.typeSearchPlaceholder': 'Name or amount',
  'inspector.cite.typeSearchCount': '{{shown}} of {{total}} types match.',
  'inspector.cite.typeLabel': 'Infraction type',
  'inspector.cite.typePlaceholder': 'Choose the type',
  'inspector.cite.typeDetail': '{{amount}} · due in {{days}} days',
  'inspector.cite.typeRequiresPhoto': 'This type demands a photograph: it cannot be issued without one.',
  'inspector.cite.typeAllowsAppeal': 'Admits an appeal',
  'inspector.cite.typeNoAppeal': 'Admits no appeal',
  'inspector.cite.typeDiscount': '{{percent}}% discount for {{days}} days',
  'inspector.cite.addressLabel': 'Written address',
  'inspector.cite.addressPlaceholder': 'South side of the market',
  'inspector.cite.notesLabel': 'Notes',
  'inspector.cite.notesPlaceholder': 'What an appeal would need to be able to read.',
  'inspector.cite.photos': 'Photographs',
  'inspector.cite.photosCount': '{{count}} of {{max}}',
  'inspector.cite.addPhoto': 'Take a photograph',
  'inspector.cite.removePhoto': 'Remove photograph',
  'inspector.cite.photoRequired': 'The photograph this infraction type demands is missing.',
  'inspector.cite.photoUnchanged':
    'Photographs are sent exactly as taken: never cropped, never recompressed.',
  'inspector.cite.photoResized':
    'This photograph is {{size}} and exceeds the upload limit ({{max}}). It is sent scaled down; that is stated here and on the citation.',
  'inspector.cite.photoTooLarge': 'The photograph is {{size}} and exceeds the {{max}} limit.',
  'inspector.cite.location': 'Location',
  'inspector.cite.locationCapture': 'Take coordinates',
  'inspector.cite.locationCaptured': '{{lat}}, {{lon}} (±{{accuracy}} m)',
  'inspector.cite.locationNone': 'No coordinates',
  'inspector.cite.locationNoneHint':
    'The citation can still be issued. It is recorded that no coordinates were taken — a position is never invented.',
  'inspector.cite.submit': 'Issue the citation',
  'inspector.cite.submitting': 'Issuing…',
  'inspector.cite.queued': 'Saved to send',
  'inspector.cite.queuedHint': 'It sends itself as soon as the signal is back.',
  'inspector.cite.issued': 'Citation {{number}} issued',
  'inspector.cite.draft': 'Citation captured as a draft',
  'inspector.cite.reset': 'Write another',
  'inspector.cite.viewCitation': 'View the citation',

  'inspector.permission.title': 'Permission needed',
  'inspector.permission.camera.why':
    'The camera is used only to attach the photograph of the infraction to the citation.',
  'inspector.permission.camera.denied':
    'Without camera access the photograph cannot be attached. Enable it in the device settings.',
  'inspector.permission.location.why':
    'The location is attached to the citation as part of the evidence. It is taken once, at issue time.',
  'inspector.permission.location.denied':
    'Without location access the citation is still issued, without coordinates.',
  'inspector.permission.allow': 'Continue',
  'inspector.permission.skip': 'Continue without it',

  'inspector.queue.title': 'Waiting to be sent',
  'inspector.queue.count.one': '1 citation pending',
  'inspector.queue.count.other': '{{count}} citations pending',
  'inspector.queue.empty': 'No citations are waiting to be sent.',
  'inspector.queue.retryAll': 'Retry sending',
  'inspector.queue.sending': 'Sending…',
  'inspector.queue.discard': 'Discard',
  'inspector.queue.discardConfirm': 'The capture is lost and no citation is issued. Discard it?',
  'inspector.queue.state.pending': 'Waiting for signal',
  'inspector.queue.state.sending': 'Sending',
  'inspector.queue.state.failed': 'Sending failed',
  'inspector.queue.state.sent': 'Sent',
  'inspector.queue.attempts.one': '1 attempt',
  'inspector.queue.attempts.other': '{{count}} attempts',
  'inspector.queue.technical': 'Technical details',
  'inspector.queue.deviceId': 'Device identifier',
  'inspector.queue.duplicateSafe':
    'Resending never duplicates: the citation carries its own device identifier, generated exactly once.',
  'inspector.queue.photosPending.one': '1 photograph left to upload',
  'inspector.queue.photosPending.other': '{{count}} photographs left to upload',

  'inspector.citations.title': 'My citations',
  'inspector.citations.empty': 'You have not issued any citation in this municipality yet.',
  'inspector.citations.detail.title': 'Citation',
  'inspector.citations.emptyFiltered': 'No citations match those filters.',
  'inspector.citations.filter.status': 'Status',
  'inspector.citations.filter.from': 'From',
  'inspector.citations.filter.to': 'To',
  'inspector.citations.filter.allStatuses': 'All statuses',
  'inspector.citations.filter.apply': 'Apply',
  'inspector.citations.filter.clear': 'Clear',

  'inspector.offline.badge': 'Offline',
  'inspector.offline.queued.one': 'Offline · 1 pending',
  'inspector.offline.queued.other': 'Offline · {{count}} pending',

  // ---- Administration: enforcement -------------------------------------------------------------

  'admin.enforcement.citations.title': 'Citations',
  'admin.enforcement.citations.description':
    'Everything this municipality’s officers issued. A citation is never edited and never deleted: it is annulled with a reason.',
  'admin.enforcement.citations.filter.status': 'Status',
  'admin.enforcement.citations.filter.zone': 'Zone',
  'admin.enforcement.citations.filter.allInspectors': 'All officers',
  'admin.enforcement.citations.filter.inspector': 'Officer',
  'admin.enforcement.citations.filter.plate': 'Plate',
  'admin.enforcement.citations.filter.from': 'From',
  'admin.enforcement.citations.filter.to': 'To',
  'admin.enforcement.citations.filter.all': 'All',
  'admin.enforcement.citations.filter.apply': 'Apply',
  'admin.enforcement.citations.filter.clear': 'Clear',
  'admin.enforcement.citations.column.number': 'Number',
  'admin.enforcement.citations.column.plate': 'Plate',
  'admin.enforcement.citations.column.infraction': 'Infraction',
  'admin.enforcement.citations.column.zone': 'Zone and bay',
  'admin.enforcement.citations.column.issuedAt': 'Issued',
  'admin.enforcement.citations.column.status': 'Status',
  'admin.enforcement.citations.column.amount': 'Amount',
  'admin.enforcement.citations.empty': 'No citation matches these filters.',
  'admin.enforcement.citations.open': 'Open',
  'admin.enforcement.citation.title': 'Citation {{number}}',
  'admin.enforcement.citation.back': 'Back to citations',
  'admin.enforcement.citation.cancel': 'Annul citation',
  'admin.enforcement.citation.cancel.title': 'Annul the citation',
  'admin.enforcement.citation.cancel.reason': 'Reason for the annulment',
  'admin.enforcement.citation.cancel.reasonHint':
    'The reason stays on the citation and in its history, and the fined person reads it too.',
  'admin.enforcement.citation.cancel.confirm': 'Annul',
  'admin.enforcement.citation.cancel.required': 'The reason is mandatory.',
  'admin.enforcement.citation.cancel.done': 'Citation annulled.',
  'admin.enforcement.citation.notCancellable': 'This citation can no longer be annulled.',
  'admin.enforcement.citation.noVoidPermission': 'Your role cannot annul citations.',

  'admin.enforcement.mapping.title': 'Causals from other systems with no equivalent',
  'admin.enforcement.mapping.description':
    'These causals arrived from another system and match nothing in the catalogue. The citations are already complete — they carry the code, the name and the amount as they arrived. What is missing is the equivalence, so reports can add them up with your own.',
  'admin.enforcement.mapping.waiting': '{{system}} · {{count}} citation(s) waiting',
  'admin.enforcement.mapping.target': 'Equivalent to',
  'admin.enforcement.mapping.choose': 'Choose an infraction from the catalogue',
  'admin.enforcement.mapping.apply': 'Apply',
  'admin.enforcement.mapping.done': '{{code}}: {{count}} citation(s) relinked.',
  'admin.enforcement.mapping.notice':
    'The equivalence only affects reports. The citation still says exactly what the other system fined the person for.',
  'admin.enforcement.types.title': 'Infraction catalogue',
  'admin.enforcement.types.description':
    'What this municipality fines, and for how much. The whole catalogue is saved at once: a row taken out is deactivated, never deleted, because citations years old reference it.',
  'admin.enforcement.types.column.code': 'Code',
  'admin.enforcement.types.column.name': 'Name',
  'admin.enforcement.types.column.amount': 'Amount',
  'admin.enforcement.types.column.photo': 'Photo required',
  'admin.enforcement.types.column.appeal': 'Appealable',
  'admin.enforcement.types.add': 'Add a type',
  'admin.enforcement.types.remove': 'Deactivate',
  'admin.enforcement.types.restore': 'Reactivate',
  'admin.enforcement.types.save': 'Save the catalogue',
  'admin.enforcement.types.saved': 'Catalogue saved.',
  'admin.enforcement.types.currencyNote': 'The currency is the municipality’s: {{currency}}.',
  'admin.enforcement.types.dueDays': 'Days to pay',
  'admin.enforcement.types.discountDays': 'Discount days',
  'admin.enforcement.types.discountPercent': 'Discount %',
  'admin.enforcement.types.discountBoth': 'The discount days and percentage travel together, or neither.',
  'admin.enforcement.types.amountLabel': 'Amount ({{currency}})',
  'admin.enforcement.types.error.required': 'Fill in the code, name, amount and due days of every row.',
  'admin.enforcement.types.inactive': 'Deactivated',

  'nav.enforcement': 'Enforcement',
  'nav.enforcement.citations': 'Citations',
  'nav.enforcement.types': 'Infractions',
  'nav.enforcement.exemptions': 'Exemptions',
  'nav.enforcement.checks': 'Lookups',
  'admin.checks.title': 'Enforcement lookups',
  'admin.checks.description':
    'Every plate an officer looked up, what the system answered, where they were and whether a citation followed. This is what you open when somebody says they were fined without anyone coming to look at the car.',
  'admin.checks.retentionNotice':
    'These rows say where an officer was during a shift. They are kept for 12 months and then deleted automatically.',
  'admin.checks.empty': 'No lookups in this period.',
  'admin.checks.filter.plate': 'Plate',
  'admin.checks.filter.inspector': 'Officer',
  'admin.checks.filter.allInspectors': 'All officers',
  'admin.checks.filter.zone': 'Zone',
  'admin.checks.filter.allZones': 'All zones',
  'admin.checks.filter.result': 'Result',
  'admin.checks.filter.allResults': 'All results',
  'admin.checks.column.when': 'Date and time',
  'admin.checks.column.inspector': 'Officer',
  'admin.checks.column.plate': 'Plate',
  'admin.checks.column.where': 'Zone and bay',
  'admin.checks.column.result': 'Result',
  'admin.checks.column.action': 'Action',
  'admin.checks.column.location': 'Location',
  'admin.checks.noZone': 'No zone',
  'admin.checks.refused': 'Refused ({{code}})',
  'admin.checks.action.cited': 'Citation issued',
  'admin.checks.action.none': 'Lookup only',
  'admin.checks.location.noFix': 'No signal at the time',
  'admin.checks.location.notGranted': 'Location not granted',
  'admin.exemptions.title': 'Exemptions',
  'admin.exemptions.description':
    'Plates this municipality does not fine for non-payment. Not a discount: charging is unchanged, what changes is that the officer sees EXEMPT instead of "no payment".',
  'admin.exemptions.create': 'Exempt a plate',
  'admin.exemptions.createNotice':
    'It hangs off the plate, not off a person or a registered vehicle: an ambulance and the council fleet almost never have an account in the app. That is why the reason and the validity are mandatory reading.',
  'admin.exemptions.empty': 'No exemptions registered.',
  'admin.exemptions.filter.status': 'State',
  'admin.exemptions.filter.all': 'All states',
  'admin.exemptions.filter.plate': 'Search by plate',
  'admin.exemptions.column.plate': 'Plate',
  'admin.exemptions.column.reason': 'Reason',
  'admin.exemptions.column.validity': 'Validity',
  'admin.exemptions.column.state': 'State',
  'admin.exemptions.status.ACTIVE': 'Registered',
  'admin.exemptions.status.REVOKED': 'Withdrawn',
  'admin.exemptions.state.inForce': 'In force',
  'admin.exemptions.state.pending': 'Not started yet',
  'admin.exemptions.state.expired': 'Expired',
  'admin.exemptions.state.revoked': 'Withdrawn',
  'admin.exemptions.validFrom': 'From {{date}}',
  'admin.exemptions.validUntil': 'Until {{date}}',
  'admin.exemptions.noEnd': 'No expiry',
  'admin.exemptions.plateHint': 'As painted. Compared without dashes or spaces.',
  'admin.exemptions.reasonHint':
    'In words, and required: it is the answer to "why was this car not fined?" when somebody asks two years from now.',
  'admin.exemptions.documentRef': 'Backing document',
  'admin.exemptions.documentRefHint': 'Agreement, memo or resolution number. The first thing asked for in a challenge.',
  'admin.exemptions.validToLabel': 'Expires on',
  'admin.exemptions.validToHint': 'Leave blank only if it truly does not expire, like the council fleet.',
  'admin.exemptions.noEndWarning':
    'With no expiry date nobody will review it. If the car is sold, it stays exempt until somebody notices.',
  'admin.exemptions.granted': 'Plate exempted.',
  'admin.exemptions.revoked': 'Exemption withdrawn.',
  'admin.exemptions.action.revoke': 'Withdraw',
  'admin.exemptions.revoke.title': 'Withdraw the exemption',
  'admin.exemptions.revoke.body': 'Plate {{plate}} is fined for non-payment again from now on.',
  'admin.exemptions.revoke.keepsRow':
    'The row is kept: it is what explains why that car was not fined while it was in force.',
  'admin.exemptions.revoke.reasonLabel': 'Reason for withdrawing',
  'admin.exemptions.error.alreadyExists': 'That plate already has a live exemption in this municipality.',
  'admin.exemptions.error.invalid': 'Check the plate, the reason and the dates.',
  'admin.exemptions.error.notActive': 'That exemption is no longer active.',
  'admin.exemptions.status.PENDING': 'Pending',
  'admin.exemptions.status.APPROVED': 'Approved',
  'admin.exemptions.status.REJECTED': 'Refused',
  'admin.exemptions.state.awaiting': 'Awaiting a decision',
  'admin.exemptions.state.rejected': 'Refused',
  'admin.exemptions.filter.type': 'Category',
  'admin.exemptions.filter.allTypes': 'All categories',
  'admin.exemptions.column.plates': 'Plates',
  'admin.exemptions.column.type': 'Category',
  'admin.exemptions.column.decision': 'Request and decision',
  'admin.exemptions.typeHint':
    "This municipality's own configuration, not a fixed list in the system: what one country exempts is not what another does.",
  'admin.exemptions.typePlaceholder': 'Choose the category',
  'admin.exemptions.beneficiaryKind': 'The permit belongs to',
  'admin.exemptions.beneficiaryKindPlaceholder': 'Person or organisation',
  'admin.exemptions.beneficiary.PERSON': 'A person',
  'admin.exemptions.beneficiary.ORGANISATION': 'An organisation',
  'admin.exemptions.beneficiaryName': "Beneficiary's name",
  'admin.exemptions.beneficiaryDocument': "Beneficiary's identity document",
  'admin.exemptions.beneficiaryDocumentHint':
    'Personal or company registration number. Seen only here: it never reaches the officer app.',
  'admin.exemptions.requestedBy': 'Requested by: {{name}}',
  'admin.exemptions.decidedBy': 'Decided by: {{name}}',
  'admin.exemptions.selfApproved': 'Requested and approved by the same person',
  'admin.exemptions.selfApprovedNotice':
    'The same person requested and approved this permit. It is allowed — a small municipality may have nobody else — and that is why it is recorded rather than hidden.',
  'admin.exemptions.requested': 'Request registered. It exempts nobody until it is approved.',
  'admin.exemptions.approved': 'Permit approved.',
  'admin.exemptions.rejected': 'Permit refused.',
  'admin.exemptions.plateAdded': 'Plate added to the permit.',
  'admin.exemptions.plateRemoved': 'Plate removed from the permit.',
  'admin.exemptions.documentAttached': 'Document attached.',
  'admin.exemptions.documentTitle': 'Document name',
  'admin.exemptions.detail.title': 'Permit detail',
  'admin.exemptions.detail.plates': 'Plates covered',
  'admin.exemptions.detail.documents': 'Backing documents',
  'admin.exemptions.detail.documentsNotice':
    'Files are not deleted from here: they are what not fining a vehicle rested on. If one is wrong, attach the right one and both stay.',
  'admin.exemptions.action.detail': 'View detail',
  'admin.exemptions.action.approve': 'Approve',
  'admin.exemptions.action.reject': 'Refuse',
  'admin.exemptions.action.addPlate': 'Add plate',
  'admin.exemptions.action.removePlate': 'Remove the plate',
  'admin.exemptions.action.submitRequest': 'Register request',
  'admin.exemptions.action.attachDocument': 'Attach document',
  'admin.exemptions.action.openDocument': 'Open',
  'admin.exemptions.reject.title': 'Refuse the permit',
  'admin.exemptions.reject.body': 'Plates {{plate}} are not exempted. The row is kept, with the reason.',
  'admin.exemptions.reject.reasonLabel': 'Reason for refusing',
  'admin.exemptions.reject.keepsRow':
    'The row is kept: a refusal is an answer somebody is owed, and it comes with the reason written down.',
  'admin.exemptions.error.notPending': 'Somebody has already decided that permit.',
  'admin.exemptions.error.notEditable': 'A refused or withdrawn permit is no longer edited.',
  'admin.exemptions.error.lastPlate': 'A permit has to cover at least one plate. If it no longer applies, withdraw it.',
  'admin.exemptions.error.plateLimit': 'A permit cannot cover more plates.',
  'admin.exemptions.error.documentLimit': 'This permit already carries the maximum number of documents.',
  'admin.exemptions.error.typeInactive': 'That category was retired and no longer takes new permits.',
  'admin.exemptions.error.documentTooLarge': 'The document is too large.',
  'admin.exemptions.error.documentType': 'Only PDF documents or photographs are accepted.',
  'nav.appeals': 'Appeals',

  // ---- Municipality: appeals -------------------------------------------------------------------

  'admin.appeals.title': 'Appeals',
  'admin.appeals.description':
    'Defences filed by citizens, oldest first. Accepting voids the citation; rejecting lets it stand. Either way you must write the reason.',
  'admin.appeals.emptyQueue': 'No appeals are waiting for a decision.',
  'admin.appeals.empty': 'No appeals with that status.',
  'admin.appeals.error': 'The appeal could not be resolved.',
  'admin.appeals.accepted': 'Appeal accepted: the citation is void.',
  'admin.appeals.rejected': 'Appeal rejected: the citation stands.',
  'admin.appeals.filter.status': 'Status',
  'admin.appeals.filter.all': 'All statuses',
  'admin.appeals.status.SUBMITTED': 'Waiting',
  'admin.appeals.status.ACCEPTED': 'Accepted',
  'admin.appeals.status.REJECTED': 'Rejected',
  'admin.appeals.status.WITHDRAWN': 'Withdrawn',
  'admin.appeals.column.filed': 'Filed',
  'admin.appeals.column.body': 'What they argue',
  'admin.appeals.column.images': 'Photos',
  'admin.appeals.noticeVersion': 'Legal notice v{{version}}',
  'admin.appeals.action.read': 'Read',
  'admin.appeals.action.citation': 'Open citation',
  'admin.appeals.action.accept': 'Accept',
  'admin.appeals.action.reject': 'Reject',
  'admin.appeals.read.title': 'The citizen’s defence',
  'admin.appeals.image': 'View photo',
  'admin.appeals.noImages': 'The citizen attached no photos.',
  'admin.appeals.alreadyResolved': 'This appeal was already decided: {{outcome}}, on {{date}}.',
  'admin.appeals.accept.title': 'Accept this appeal?',
  'admin.appeals.accept.body': 'The citation becomes void and the citizen no longer owes it.',
  'admin.appeals.reject.title': 'Reject this appeal?',
  'admin.appeals.reject.body': 'The citation stands and becomes payable again from today.',
  'admin.appeals.reasonNotice':
    'The citizen reads this reason, and it stays in the citation’s history. Write it for someone who was not there.',
  'admin.appeals.reasonLabel': 'Reason for the decision',
  'admin.appeals.reasonPlaceholder': 'The photograph on the citation shows…',

  // ---- Citizen: fines --------------------------------------------------------------------------

  'citizen.fines.detail.title': 'Fine',
  'citizen.fines.amountOriginal': 'Original amount',
  'citizen.fines.amountPayable': 'Amount payable today',
  'citizen.fines.discountUntil': 'Discounted until {{date}}',
  'citizen.fines.pay': 'Pay the fine',
  'citizen.fines.appealable': 'Admits an appeal',
  'citizen.fines.notAppealable': 'Admits no appeal',
  'citizen.fines.empty.history': 'There are no fines in this municipality’s history.',
  'citizen.fines.open': 'Open',
  'citizen.fines.appeal': 'File an appeal',
  'citizen.fines.viewAppeal': 'View my appeal',

  // ---- Citizen: appeal -------------------------------------------------------------------------

  'citizen.appeal.title': 'Appeal',
  'citizen.appeal.notice.title': 'Before you write',
  'citizen.appeal.notice.version': 'Version {{version}}, in force since {{date}}',
  'citizen.appeal.write.title': 'Your appeal',
  'citizen.appeal.write.description':
    'Say what happened and why you believe the citation is wrong. Photos are added after the text is saved.',
  'citizen.appeal.write.label': 'Appeal text',
  'citizen.appeal.write.placeholder': 'That day I had paid from the app and still had time left…',
  'citizen.appeal.write.count': '{{used}} of {{max}} characters',
  'citizen.appeal.acceptNotice': 'By filing it you accept the legal notice shown above.',
  'citizen.appeal.attach.title': 'Attach evidence (optional)',
  'citizen.appeal.attach.hint': 'Up to 3 images from your gallery. Large ones are shrunk so they can be sent.',
  'citizen.appeal.attach.select': 'Select images',
  'citizen.appeal.attach.remove': 'Remove',
  'citizen.appeal.attach.reduced': 'shrunk',
  'citizen.appeal.attach.error.type': 'Unsupported format. Use JPG, PNG or WebP.',
  'citizen.appeal.attach.error.max': 'Maximum {{max}} images.',
  'citizen.appeal.attach.error.prepare': 'This image could not be prepared. Try another.',
  'citizen.appeal.submit': 'File appeal',
  'citizen.appeal.notAppealable': 'This fine cannot be appealed.',
  'citizen.appeal.waiting': 'The municipality has not decided your appeal yet. In the meantime the fine is not charged.',
  'citizen.appeal.filed.title': 'Appeal filed',
  'citizen.appeal.filed.submittedAt': 'Filed on {{date}}',
  'citizen.appeal.decision.title': 'The municipality’s decision',
  'citizen.appeal.decision.accepted': 'Your appeal was accepted: the fine is void.',
  'citizen.appeal.decision.rejected': 'Your appeal was rejected: the fine stands and is payable again.',
  'citizen.appeal.images.title': 'Photos with the appeal',
  'citizen.appeal.images.count': '{{used}} of {{max}} allowed by this municipality',
  'citizen.appeal.images.item': 'Photo {{number}} · {{date}}',
  'citizen.appeal.images.camera': 'Take a photo with the camera',
  'citizen.appeal.images.add': 'Add photo',
  'citizen.appeal.error.generic': 'The appeal could not be filed.',
  'citizen.appeal.error.alreadyFiled': 'An appeal has already been filed against this fine.',
  'citizen.appeal.error.noticeOutdated':
    'The municipality changed the legal notice while you were writing. Read it again above and file once more; your text was not lost.',
  'citizen.appeal.error.noticeUnavailable': 'The legal notice could not be loaded, so the appeal cannot be filed.',
  'citizen.appeal.error.imageLimit': 'You have reached the maximum number of photos this municipality allows.',
  'citizen.appeal.error.imageTooLarge': 'That photo is too large. It must be 1 MB or less.',
  'citizen.appeal.error.imageFailed': 'The photo could not be added. Your appeal is already filed.',

  'citizen.fines.pay.confirmTitle': 'Pay this fine?',
  'citizen.fines.pay.amount': '{{amount}} will be taken from your wallet.',
  'citizen.fines.pay.discountNotice': 'That is the reduced amount; after {{date}} it rises to {{full}}.',
  'citizen.fines.pay.balanceAfter': 'You would have {{amount}} left.',
  'citizen.fines.pay.withdrawsAppeal':
    'Paying withdraws the claim you filed: the municipality will no longer resolve it. This cannot be undone.',
  'citizen.fines.pay.submit': 'Yes, pay',
  'citizen.fines.pay.success': 'Fine paid.',
  'citizen.fines.pay.successWithdrew': 'Fine paid. Your claim was withdrawn.',
  'citizen.fines.pay.topUp': 'Add balance',
  'citizen.fines.pay.error.INSUFFICIENT_BALANCE': 'Your balance does not cover this fine.',
  'citizen.fines.pay.error.CITATION_NOT_PAYABLE': 'This fine can no longer be paid: it may already be paid or void.',
  'citizen.fines.pay.error.CITATION_NOT_MANAGED_HERE': 'The municipality collects this fine in its other system.',
  'citizen.fines.pay.error.APPEAL_BY_ANOTHER_CITIZEN':
    'Somebody else filed the claim waiting on this fine, so paying it here would close their claim. The municipality has to resolve it first.',

  'appeal.status.withdrawn': 'Withdrawn',
  'citizen.appeal.withdrawn': 'You withdrew this claim when you paid the fine.',

  'appeal.status.submitted': 'Waiting',
  'appeal.status.accepted': 'Accepted',
  'appeal.status.rejected': 'Rejected',

  'geo.level.CR.1': 'Province',
  'geo.level.CR.2': 'Canton',
  'geo.level.CR.3': 'District',
  'geo.level.ES.1': 'Autonomous community',
  'geo.level.ES.2': 'Province',
  'geo.level.ES.3': 'Municipality',
  'geo.level.MX.1': 'State',
  'geo.level.MX.2': 'Municipality',
  'geo.level.PA.1': 'Province',
  'geo.level.PA.2': 'District',
  'geo.level.PA.3': 'Township',
  'geo.level.US.1': 'State',
  /* ---- Device: the browser camera, shared by Citizen and Enforcement ---- */
  'device.camera.title': 'Camera',
  'device.camera.state.granted': 'Enabled',
  'device.camera.state.asksOnUse': 'Asked for on use',
  'device.camera.state.denied': 'Blocked',
  'device.camera.state.untested': 'Not checked',
  'device.camera.state.unsupported': 'Not available in this browser',
  'device.camera.state.insecure': 'Not available without a secure connection',
  'device.camera.state.notFound': 'No camera on this device',
  'device.camera.state.busy': 'Another application is using it',
  'device.camera.state.failed': 'Could not be opened',
  'device.camera.test': 'Check the camera',
  'device.camera.enable': 'Enable camera',
  'device.camera.testing': 'Checking…',
  'device.camera.note.denied': 'The permission is blocked for this site. Enable it from the padlock in the address bar, under site permissions, and check again here.',
  'device.camera.note.insecure': 'Browsers only hand over the camera on secure pages (https). Open the https address and try again.',
  'device.camera.note.unsupported': 'This browser cannot use the camera from a page. You can attach a photograph you already took.',
  'device.camera.note.notFound': 'No camera was found. You can attach a photograph you already took.',
  'device.camera.note.busy': 'Another application is holding the camera. Close it and check again.',
  'device.camera.note.failed': 'The camera could not be opened. You can attach a photograph you already took.',
  'device.camera.note.asksOnUse': 'The browser asks for it at the moment of use. Check it here to find out whether it really answers.',
  'device.camera.note.granted': 'Checked: the device delivered an image.',
  'device.capture.title': 'Take a photograph',
  'device.capture.testTitle': 'Test the camera',
  'device.capture.close': 'Close',
  'device.capture.testOk': 'The camera is delivering an image. Closing turns it off.',
  'device.capture.shutter': 'Take photo',
  'device.capture.again': 'Retake',
  'device.capture.use': 'Use this photo',
  'device.capture.cancel': 'Cancel',
  'device.capture.opening': 'Opening the camera…',
  'device.capture.preview': 'Preview of what will be saved',
  'device.capture.live': 'What the camera sees right now',
  'device.capture.attach': 'Attach an image',
  'device.capture.failedFrame': 'The frame could not be captured. Try again.',

  'geo.level.US.2': 'County',
  'geo.level.US.3': 'City',
};
